<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  nextTick,
  onMounted,
  onUnmounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useRoute, useRouter } from 'vue-router';
import SearchAPI from 'dashboard/api/search';
import AceleresTeamPresenceAPI from 'dashboard/api/aceleresTeamPresence';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import { Virtualizer } from 'virtua/vue';
import ChatListHeader from './ChatListHeader.vue';
import StarredMessagesPanel from './StarredMessagesPanel.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import QuickConversationFilter from 'next/filter/QuickConversationFilter.vue';
import AceleresDropdownFilter from './AceleresDropdownFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import AceleresNewConversationPanel from './AceleresNewConversationPanel.vue';
import AceleresStartConversationFlow from './AceleresStartConversationFlow.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import AclSelectionToolbar from './widgets/conversation/AclSelectionToolbar.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';
import { isAceleresConversationStarted } from 'dashboard/helper/aceleresConversationState';
import { filterServerValidatedConversations } from 'dashboard/helper/conversationPrefetchCache';
import { waitForZoraxyCriticalData } from 'dashboard/helper/zoraxyStartupLoading';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import { shouldRunChatListAutoRefresh } from 'dashboard/helper/chatListAutoRefresh';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
// Aceleres: arquivadas são um estado interno do histórico, mas a interface
// exibe concluídas e arquivadas juntas na única aba "Concluídos".
const shouldMergeArchivedIntoConcluded = ref(true);

// ─── Aceleres: quick search com dropdown live (WhatsApp Web-style) ─────────
const quickSearch = ref('');
const quickSearchResults = ref({ contacts: [], conversations: [] });
const quickSearchOpen = ref(false);
const quickSearchLoading = ref(false);
let quickSearchTimer = null;
let quickSearchSeq = 0;

const accountIdForQuickSearch = useMapGetter('getCurrentAccountId');

function normalizeQuickSearchQuery(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim();
}

async function performQuickSearch(q) {
  quickSearchOpen.value = true;
  quickSearchLoading.value = true;
  const seq = ++quickSearchSeq;
  const personalBoxActive =
    normalizeAceleresBoxQuery(route.query[ACELERES_BOX_QUERY_PARAM]) ===
    'pessoais';
  try {
    const r = await SearchAPI.conversations({
      q,
      status: wootConstants.STATUS_TYPE.ALL,
      excludePersonal: false,
      aceleresBox: personalBoxActive ? 'pessoais' : undefined,
    });
    if (seq !== quickSearchSeq) return; // resultado defasado, ignora
    const payload = r?.data?.payload || {};
    quickSearchResults.value = {
      contacts: payload.contacts || [],
      conversations: payload.conversations || [],
    };
  } catch (e) {
    if (seq === quickSearchSeq) {
      quickSearchResults.value = { contacts: [], conversations: [] };
    }
    console.warn('quickSearch fail', e);
  } finally {
    if (seq === quickSearchSeq) quickSearchLoading.value = false;
  }
}

function onQuickSearchInput() {
  const q = normalizeQuickSearchQuery(quickSearch.value);
  if (quickSearchTimer) clearTimeout(quickSearchTimer);
  if (q.length < 2) {
    quickSearchSeq += 1;
    quickSearchLoading.value = false;
    quickSearchResults.value = { contacts: [], conversations: [] };
    quickSearchOpen.value = q.length >= 1; // mostra CTA novo número se for phone-like
    return;
  }

  quickSearchOpen.value = true;
  quickSearchLoading.value = true;
  quickSearchTimer = setTimeout(() => performQuickSearch(q), 300);
}

function onQuickSearchEnter() {
  const q = normalizeQuickSearchQuery(quickSearch.value);
  if (q.length < 2) return;

  // Enter sempre confirma a pesquisa, inclusive para telefone. O botão
  // "Iniciar nova conversa" continua separado e só aparece sem resultados.
  if (quickSearchTimer) clearTimeout(quickSearchTimer);
  performQuickSearch(q);
}

function openConvFromQuick(conv) {
  const accId = accountIdForQuickSearch.value;
  router.push(`/app/accounts/${accId}/conversations/${conv.id}`);
}

function openContactFromQuick(contact) {
  const accId = accountIdForQuickSearch.value;
  router.push(`/app/accounts/${accId}/contacts/${contact.id}`);
}

function closeQuickSearch() {
  quickSearchOpen.value = false;
  quickSearch.value = '';
  quickSearchResults.value = { contacts: [], conversations: [] };
  startConversationFlowRef.value?.reset();
}

function softCloseQuickSearch() {
  quickSearchOpen.value = false;
  startConversationFlowRef.value?.reset();
}

const quickSearchIsPhone = computed(() => {
  const digits = (quickSearch.value || '').replace(/\D/g, '');
  return digits.length >= 8;
});
const quickSearchNoResults = computed(
  () =>
    !quickSearchLoading.value &&
    quickSearch.value.length >= 2 &&
    quickSearchResults.value.contacts.length === 0 &&
    quickSearchResults.value.conversations.length === 0
);
const isSearchActive = computed(
  () => quickSearchOpen.value && quickSearch.value.length >= 1
);
// Aceleres: contatos do quick search ocultam os sem telefone (so email)
const quickSearchContacts = computed(() =>
  (quickSearchResults.value.contacts || []).filter(c => c.phone_number)
);

// A contact can have more than one historical conversation (different
// instances, reaberturas or conversas concluídas). The quick search is a
// contact launcher, so showing every historical row made the same person
// appear duplicated and made it unclear which result to open. Keep the first
// result returned by the API: it is already ordered by latest activity.
const quickSearchConversations = computed(() => {
  const seenContacts = new Set();
  return (quickSearchResults.value.conversations || []).filter(conversation => {
    const contactKey = conversation.contact?.id || conversation.contact?.phone_number;
    if (!contactKey) return true;
    if (seenContacts.has(contactKey)) return false;
    seenContacts.add(contactKey);
    return true;
  });
});

function isQuickSearchConversationActive(conversationId) {
  return Number(conversationId) === activeQuickSearchConversationId.value;
}

const START_PHONE_QUERY_PARAM = 'start_phone';
const ACELERES_BOX_QUERY_PARAM = 'aceleres_box';

// Aceleres: fluxo compartilhado de nova conversa (seleção de instância,
// ownership e criação). O telefone chega do painel deslizante
// (AceleresNewConversationPanel) ou do quick-search.
const startConversationFlowRef = ref(null);
const isStartingConversation = computed(
  () => startConversationFlowRef.value?.isBusy || false
);
const showNewConversationPanel = ref(false);
const showNewContactInEmptyState = ref(false);
const showStarredMessages = ref(false);
const EMPTY_STATE_NEW_CONTACT_EVENT = 'acl:open-new-contact-in-empty-state';
const newConversationPanelTeleportTarget = computed(() =>
  showNewContactInEmptyState.value ? '.acl-vazio-chat' : null
);

function openStarredMessages() {
  closeQuickSearch();
  showNewConversationPanel.value = false;
  showStarredMessages.value = true;
}

function closeStarredMessages() {
  showStarredMessages.value = false;
}

async function openStarredMessage(message) {
  showStarredMessages.value = false;
  await router.push({
    path: `/app/accounts/${accountIdForQuickSearch.value}/conversations/${message.conversation_id}`,
    query: { messageId: message.id },
  });
  window.requestAnimationFrame(() => {
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId: message.id });
  });
}

function openHeaderNewConversation() {
  showNewContactInEmptyState.value = false;
  showNewConversationPanel.value = true;
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
}

function closeHeaderNewConversation() {
  showNewConversationPanel.value = false;
  showNewContactInEmptyState.value = false;
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
}

function openNewContactFromEmptyState() {
  showNewContactInEmptyState.value = true;
  showNewConversationPanel.value = true;
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
}

function startConversationFromPanel(phone) {
  closeHeaderNewConversation();
  openNewConversationForPhone(phone);
}

function openContactChatFromPanel(contact) {
  closeHeaderNewConversation();
  if (contact?.phone_number) {
    openNewConversationForPhone(contact.phone_number);
    return;
  }
  openContactFromQuick(contact);
}

function editContactFromPanel(contact) {
  closeHeaderNewConversation();
  openContactFromQuick(contact);
}

const currentUser = useMapGetter('getCurrentUser');

// Delega para o fluxo compartilhado (seleção de instância + ownership + criação
// da conversa), o mesmo usado pela página de Contatos.
async function openNewConversationForPhone(phone = null) {
  const q = (phone || quickSearch.value || '').trim();
  if (!q) return;
  // O deep link ?start_phone= dispara ainda durante o setup, antes de o fluxo
  // estar montado.
  if (!startConversationFlowRef.value) await nextTick();
  startConversationFlowRef.value?.startForPhone(q);
}

const store = useStore();

const resolveAttributesModalRef = ref(null);
const conversationListRef = ref(null);
const virtualListRef = ref(null);

provide('contextMenuElementTarget', virtualListRef);

// Aceleres: caixas operacionais Novos/Meus/Outros/Pessoais;
// a abertura padrão é Novos, e Outros aparece para gestores e supervisores.
const aceleresDefaultAssigneeTab = 'novos';
const UNREAD_SUBFILTER_ALL = 'all';
const UNREAD_SUBFILTER_ONLY = 'unread';
const activeAssigneeTab = ref(aceleresDefaultAssigneeTab);
const unreadSubfiltersByTab = ref({
  novos: UNREAD_SUBFILTER_ALL,
  [wootConstants.ASSIGNEE_TYPE.ME]: UNREAD_SUBFILTER_ALL,
  outros: UNREAD_SUBFILTER_ALL,
  [wootConstants.ASSIGNEE_TYPE.PERSONAL]: UNREAD_SUBFILTER_ALL,
});
const aceleresUserPickedTab = ref(false);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const activeInboxFilter = ref(null);
const iaOnly = ref(false);
const qfPriority = ref(null);
const qfLabelSlug = ref(null);
const qfAgentId = ref(null);
const qfTeamId = ref(null);
const isManagerRole = computed(
  () => store.getters.getCurrentRole === 'administrator'
);
const showAdvancedFilters = ref(false);
const aceleresFilterApplied = ref(false);
const aceleresFilterCount = ref(0);
const aceleresFilterMenuOpen = ref(false);

function getAceleresSessionValue(key, factory) {
  if (typeof window === 'undefined') {
    return factory();
  }

  if (!Reflect.has(window, key)) {
    Reflect.set(window, key, factory());
  }
  return Reflect.get(window, key);
}

// Aceleres: true quando o filtro aplicado inclui "Atendidos pela IA"
// (attribute_key atendido_ia). Alimenta o empty state contextual.
const appliedIaFilter = ref(false);
const aceleresDropdownFilterRef = ref(null);
// Aceleres: assinatura (filtros+pagina+tamanho da lista) do ultimo fetch
// disparado pelo sentinel — impede refetch identico em loop.
const aceleresLastLoadMoreKey = ref(null);
const aceleresConversationCache = getAceleresSessionValue(
  '__aceleresConversationTabCache:v3',
  () => new Map()
);
const aceleresCompleteConversationCacheKeys = getAceleresSessionValue(
  '__aceleresCompleteConversationTabCacheKeys:v3',
  () => new Set()
);
const aceleresConversationCacheVersion = ref(0);
const ACELERES_CONVERSATION_CACHE_LIMIT = 32;
const ACELERES_STATS_CACHE_TTL = 60000;
const ACELERES_CONVERSATIONS_PER_PAGE = 25;
// O prefetch de mensagens e apenas uma otimizacao de abertura. Manter poucos
// itens aqui deixa a lista aparecer antes; o restante continua carregando ao
// abrir a conversa.
const ACELERES_STARTUP_MESSAGE_CONVERSATIONS = 4;
const ACELERES_STARTUP_MESSAGE_LIMIT = 10;
const CONCLUDED_ASSIGNEE_TAB = 'concluidos';
const CONCLUDED_TAB_LABEL = 'Concluídos';
const ARCHIVED_ASSIGNEE_TAB = 'arquivadas';
const isHistoryTab = tab =>
  [CONCLUDED_ASSIGNEE_TAB, ARCHIVED_ASSIGNEE_TAB].includes(tab);
const ACELERES_TAB_BOXES = {
  novos: 'novos',
  [wootConstants.ASSIGNEE_TYPE.ME]: 'meus',
  outros: 'outros',
  [wootConstants.ASSIGNEE_TYPE.PERSONAL]: 'pessoais',
  [CONCLUDED_ASSIGNEE_TAB]: 'concluidos',
  [ARCHIVED_ASSIGNEE_TAB]: 'arquivadas',
};
const isOperationalAceleresTab = tab =>
  Object.hasOwn(ACELERES_TAB_BOXES, tab) && !isHistoryTab(tab);
let aceleresStartupPrefetchRequests = 0;
let aceleresCriticalMenuDataPromise = null;
// As caixas principais e as mensagens da primeira página são carregadas no
// boot. Assim, trocar de aba só troca a visão local já disponível.
const aceleresStartupTabsReady = ref(false);
let aceleresStartupTabsPromise = null;
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
// Evita exibir a lista anterior durante a troca de caixa/subfiltro. A chave
// garante que uma resposta atrasada não encerre a transição da visão atual.
const aceleresViewTransitioning = ref(false);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const activeAceleresFilterConditions = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const allConversationsList = useMapGetter('getAllConversations');
const conversationStatusSnapshot = computed(() =>
  (allConversationsList.value || [])
    .map(conversation => `${conversation?.id}:${conversation?.status}`)
    .join('|')
);
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const chatListHasError = useMapGetter('getChatListErrorStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const currentChat = useMapGetter('getSelectedChat');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');

watch(
  () => route.query[START_PHONE_QUERY_PARAM],
  async phoneQuery => {
    const phone = Array.isArray(phoneQuery) ? phoneQuery[0] : phoneQuery;
    if (!phone) return;

    if (!inboxesList.value?.length) {
      await store.dispatch('inboxes/get');
    }

    openNewConversationForPhone(phone);
    const nextQuery = { ...route.query };
    delete nextQuery[START_PHONE_QUERY_PARAM];
    router.replace({
      name: route.name,
      params: route.params,
      query: nextQuery,
    });
  },
  { immediate: true }
);
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');
const activeQuickSearchConversationId = computed(() =>
  Number(currentChat.value?.id || route.params.conversation_id || 0)
);

const supervisedTeamIds = computed(() =>
  (teamsList.value || [])
    .filter(team => team.is_supervisor)
    .map(team => Number(team.id))
    .filter(id => !Number.isNaN(id))
);
const isSupervisorUser = computed(() => supervisedTeamIds.value.length > 0);
const currentUserTeamIds = computed(() => {
  const currentUserId = Number(currentUser.value?.id);
  const currentAgent = (agentList.value || []).find(
    agent => Number(agent.id) === currentUserId
  );
  const agentTeamIds = (currentAgent?.teams || []).map(team => Number(team.id));
  const userTeamIds = (currentUser.value?.teams || []).map(team => Number(team.id));
  const listedTeamIds = (teamsList.value || [])
    .filter(team => team?.is_member || team?.is_supervisor)
    .map(team => Number(team.id));

  return [...new Set([...agentTeamIds, ...userTeamIds, ...listedTeamIds])].filter(
    id => !Number.isNaN(id)
  );
});
const managerTeamIds = computed(() => {
  if (!isManagerRole.value) return [];

  const currentUserId = Number(currentUser.value?.id);
  const currentAgent = (agentList.value || []).find(
    agent => Number(agent.id) === currentUserId
  );
  const agentTeamIds = (currentAgent?.teams || []).map(team => Number(team.id));
  const userTeamIds = (currentUser.value?.teams || []).map(team => Number(team.id));

  return [...new Set([...agentTeamIds, ...userTeamIds])].filter(
    id => !Number.isNaN(id)
  );
});
const managerSellerIds = computed(() => {
  if (!isManagerRole.value) return [];

  const sellers = (agentList.value || []).filter(
    agent => agent.role === 'agent'
  );
  const teamIds = managerTeamIds.value;

  if (!teamIds.length) {
    return sellers.map(agent => Number(agent.id));
  }

  return sellers
    .filter(agent =>
      (agent.teams || []).some(team => teamIds.includes(Number(team.id)))
    )
    .map(agent => Number(agent.id));
});
// A aba "Outros" é uma visão de acompanhamento da equipe supervisionada.
// Gestor sem o papel de supervisor não deve receber essa caixa; usuários com
// os dois perfis continuam autorizados por isSupervisorUser.
const canSeeOthersBox = computed(() => isSupervisorUser.value);
// Espelha managerSellerIds, mas com base nas equipes de SUPERVISÃO
// (supervisedTeamIds), igual ao supervised_scope do backend
// (conversation_finder.rb) — "Outros" nunca deve usar as equipes de gestor.
const supervisedSellerIds = computed(() => {
  if (!isSupervisorUser.value) return [];

  const sellers = (agentList.value || []).filter(
    agent => agent.role === 'agent'
  );
  const teamIds = supervisedTeamIds.value;

  return sellers
    .filter(agent =>
      (agent.teams || []).some(team => teamIds.includes(Number(team.id)))
    )
    .map(agent => Number(agent.id));
});
const canSeeUnassignedLeads = computed(
  () => isManagerRole.value || isSupervisorUser.value
);
const ownedPersonalInboxIds = computed(() => {
  const currentUserId = Number(currentUser.value?.id);
  if (!currentUserId) {
    return [];
  }

  return (inboxesList.value || [])
    .filter(inbox => {
      const attrs = inbox.additional_attributes || {};
      return (
        isAceleresIndividualOwn(attrs.connection_type) &&
        Number(attrs.owner_chatwoot_user_id) === currentUserId
      );
    })
    .map(inbox => Number(inbox.id))
    .filter(id => !Number.isNaN(id));
});
// A aba Pessoais só existe quando o usuário possui uma inbox individual_own
// vinculada a ele. O estado de conexão não muda a existência da instância.
const shouldShowPersonalTab = computed(
  () => ownedPersonalInboxIds.value.length > 0
);

const hasPersonalConversationMarker = conversation => {
  const conversationLabels = Array.isArray(conversation?.labels)
    ? conversation.labels
    : [];
  const attrs =
    conversation?.additional_attributes ||
    conversation?.additionalAttributes ||
    {};
  const inboxId = Number(conversation?.inbox_id ?? conversation?.inboxId ?? 0);

  return (
    conversationLabels.includes('pessoal') ||
    Number(
      attrs.personal_owner_chatwoot_user_id ||
        attrs.personalOwnerChatwootUserId ||
        0
    ) > 0 ||
    Number(
      attrs.personal_origin_chatwoot_user_id ||
        attrs.personalOriginChatwootUserId ||
        0
    ) > 0 ||
    ownedPersonalInboxIds.value.includes(inboxId)
  );
};

const isPersonalConversationForCurrentUser = conversation => {
  const currentUserId = Number(currentUser.value?.id || 0);
  const attrs =
    conversation?.additional_attributes ||
    conversation?.additionalAttributes ||
    {};
  const conversationLabels = Array.isArray(conversation?.labels)
    ? conversation.labels
    : [];
  const inboxId = Number(conversation?.inbox_id ?? conversation?.inboxId ?? 0);
  const personalOwnerId = Number(
    attrs.personal_owner_chatwoot_user_id ||
      attrs.personalOwnerChatwootUserId ||
      0
  );

  return (
    ownedPersonalInboxIds.value.includes(inboxId) ||
    (conversationLabels.includes('pessoal') &&
      personalOwnerId === currentUserId)
  );
};

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent: bulkAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const filterValueNames = values => {
  const normalizedValues = Array.isArray(values) ? values : [values];

  return normalizedValues
    .map(value => {
      if (value && typeof value === 'object') {
        return value.name || value.id;
      }

      return value;
    })
    .map(value => String(value || '').trim())
    .filter(Boolean);
};

const labelNamesFromConditions = conditions =>
  (Array.isArray(conditions) ? conditions : [])
    .filter(condition => {
      const attributeKey = condition.attributeKey || condition.attribute_key;
      const filterOperator =
        condition.filterOperator || condition.filter_operator;
      return attributeKey === 'labels' && filterOperator === 'equal_to';
    })
    .flatMap(condition => filterValueNames(condition.values));

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const previousStatusBeforeConcludedTab = ref(wootConstants.STATUS_TYPE.OPEN);
const aceleresTabToQuery = {
  novos: 'novos',
  [wootConstants.ASSIGNEE_TYPE.ME]: 'meus',
  outros: 'outros',
  [wootConstants.ASSIGNEE_TYPE.PERSONAL]: 'pessoais',
  [CONCLUDED_ASSIGNEE_TAB]: 'concluidos',
  [ARCHIVED_ASSIGNEE_TAB]: 'arquivadas',
};
const aceleresQueryToTab = {
  todas: 'novos',
  novos: 'novos',
  meus: wootConstants.ASSIGNEE_TYPE.ME,
  outros: 'outros',
  pessoais: wootConstants.ASSIGNEE_TYPE.PERSONAL,
  concluidos: CONCLUDED_ASSIGNEE_TAB,
  arquivadas: ARCHIVED_ASSIGNEE_TAB,
};

function normalizeAceleresBoxQuery(value) {
  const rawValue = Array.isArray(value) ? value[0] : value;
  return typeof rawValue === 'string' ? rawValue.trim().toLowerCase() : '';
}

function tabFromAceleresBoxQuery(value) {
  const selectedTab =
    aceleresQueryToTab[normalizeAceleresBoxQuery(value)] || null;

  if (
    selectedTab === wootConstants.ASSIGNEE_TYPE.PERSONAL &&
    !shouldShowPersonalTab.value
  ) {
    return null;
  }

  if (
    shouldMergeArchivedIntoConcluded.value &&
    selectedTab === ARCHIVED_ASSIGNEE_TAB
  ) {
    return CONCLUDED_ASSIGNEE_TAB;
  }

  return selectedTab;
}

function queryFromAceleresTab(tab) {
  return aceleresTabToQuery[tab] || null;
}

function aceleresBoxForTab(tab) {
  if (
    shouldMergeArchivedIntoConcluded.value &&
    tab === CONCLUDED_ASSIGNEE_TAB
  ) {
    return undefined;
  }

  return ACELERES_TAB_BOXES[tab] || undefined;
}

function setAceleresBoxQuery(tab) {
  const nextBox = queryFromAceleresTab(tab);
  if (!nextBox) {
    return;
  }

  const currentBox = normalizeAceleresBoxQuery(
    route.query[ACELERES_BOX_QUERY_PARAM]
  );
  if (currentBox === nextBox) {
    return;
  }

  router.replace({
    name: route.name,
    params: route.params,
    query: {
      ...route.query,
      [ACELERES_BOX_QUERY_PARAM]: nextBox,
    },
  });
}

function syncAssigneeTabFromRoute({
  immediateStats = false,
  fetchData = true,
} = {}) {
  const rawBox = normalizeAceleresBoxQuery(
    route.query[ACELERES_BOX_QUERY_PARAM]
  );
  const selectedTab = tabFromAceleresBoxQuery(
    route.query[ACELERES_BOX_QUERY_PARAM]
  );

  if (rawBox === 'pessoais' && !shouldShowPersonalTab.value) {
    setAceleresBoxQuery('novos');
    return;
  }

  if (rawBox === 'todas') {
    setAceleresBoxQuery('novos');
  }

  if (rawBox === 'arquivadas' && shouldMergeArchivedIntoConcluded.value) {
    setAceleresBoxQuery(CONCLUDED_ASSIGNEE_TAB);
  }

  if (!selectedTab || activeAssigneeTab.value === selectedTab) {
    return;
  }

  updateAssigneeTab(selectedTab, {
    immediateStats,
    fetchData,
    updateRouteQuery: false,
  });
}

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

// Aceleres: versoes "ForUI" das flags — consideram filtros aplicados EXCETO o
// filtro Aceleres (que tem UI propria). Mantem abas/header usaveis enquanto o
// filtro Aceleres esta ativo; folders continuam escondendo as abas. (Sem isso,
// essas refs ficavam indefinidas no template.)
const hasAppliedFiltersForUI = computed(
  () => hasAppliedFilters.value && !aceleresFilterApplied.value
);
const hasAppliedFiltersOrActiveFoldersForUI = computed(
  () => hasAppliedFiltersForUI.value || hasActiveFolders.value
);

const activeConversationLabel = computed(() => {
  const selectedLabel = qfLabelSlug.value || props.label;
  return selectedLabel;
});

const activeLabelFilterNames = computed(() => {
  const names = [
    activeConversationLabel.value,
    ...labelNamesFromConditions(activeAceleresFilterConditions.value),
    ...labelNamesFromConditions(appliedFilters.value),
  ]
    .map(label => String(label || '').trim())
    .filter(Boolean);

  return [...new Set(names)];
});

const activeTabLabel = computed(() => {
  const tabLabels = {
    novos: 'Novos',
    me: 'Meus',
    outros: 'Outros',
    personal: 'Pessoais',
    resolved: 'Concluídos',
    arquivadas: 'Arquivadas',
  };

  return tabLabels[activeAssigneeTab.value] || 'Conversas';
});

const labelFilterEmptyMessage = computed(() => {
  if (!activeLabelFilterNames.value.length) return '';

  return t('CHAT_LIST.FILTERED_EMPTY.WITH_LABELS', {
    labels: activeLabelFilterNames.value.join(', '),
    tab: activeTabLabel.value,
  });
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const unreadMineCount = computed(() => {
  try {
    // Aceleres: a aba "Novas" (mineUnread badge) deve ter contagem GLOBAL,
    // independente da aba ativa. Quando o usuario esta em "Pessoais",
    // conversationFilters sintetiza labels:[pessoal]; getMineChats aplica
    // applyPageFilters por labels e filtra fora todas as conversas "minhas"
    // sem essa label, zerando o badge. Removemos o labels antes de chamar
    // mineChatsList quando assigneeType === personal.
    const f = { ...conversationFilters.value };
    if (f.assigneeType === 'personal') {
      delete f.labels;
    }
    const list = mineChatsList.value(f) || [];
    return list.filter(c => (c.unread_count || 0) > 0).length;
  } catch (e) {
    return 0;
  }
});

const isVisibleConcludedConversationForCurrentUser = conversation => {
  if (conversation?.status !== wootConstants.STATUS_TYPE.RESOLVED) {
    return false;
  }

  if (!isSupervisorUser.value) {
    return true;
  }

  return !(
    Array.isArray(conversation?.labels) &&
    conversation.labels.includes('pessoal')
  );
};

const isArchivedConversation = conversation => {
  const value = conversation?.additional_attributes?.aceleres_archived;
  return value === true || value === 'true';
};

const isVisibleConversationForHistoryTab = conversation => {
  if (!isVisibleConcludedConversationForCurrentUser(conversation)) {
    return false;
  }

  if (
    shouldMergeArchivedIntoConcluded.value &&
    activeAssigneeTab.value === CONCLUDED_ASSIGNEE_TAB
  ) {
    return true;
  }

  return activeAssigneeTab.value === ARCHIVED_ASSIGNEE_TAB
    ? isArchivedConversation(conversation)
    : !isArchivedConversation(conversation);
};

const conversationCountForTab = tab => {
  const box = ACELERES_TAB_BOXES[tab];
  const unreadCount = conversationStats.value?.aceleresBoxUnreadCounts?.[box];
  return typeof unreadCount === 'number' ? unreadCount : 0;
};

const conversationTotalCountForTab = tab => {
  const box = ACELERES_TAB_BOXES[tab];
  const totalCount = conversationStats.value?.aceleresBoxCounts?.[box];
  return typeof totalCount === 'number' ? totalCount : 0;
};

// A paginação e o contador visual usam o total de conversas. A contagem de
// não lidas permanece separada no estado para os subfiltros da lista.
const concludedConversationsCount = computed(() => {
  const concludedCount = conversationStats.value?.aceleresBoxCounts?.concluidos;
  if (typeof concludedCount === 'number') {
    return concludedCount;
  }

  const resolvedCountFromStats = conversationStats.value?.resolvedCount;
  const resolvedPersonalCountFromStats =
    conversationStats.value?.resolvedPersonalCount || 0;

  if (typeof resolvedCountFromStats === 'number') {
    return isSupervisorUser.value
      ? Math.max(0, resolvedCountFromStats - resolvedPersonalCountFromStats)
      : resolvedCountFromStats;
  }

  return (allConversationsList.value || []).filter(conversation => {
    if (!isVisibleConcludedConversationForCurrentUser(conversation)) {
      return false;
    }

    return (
      shouldMergeArchivedIntoConcluded.value ||
      !isArchivedConversation(conversation)
    );
  }).length;
});

const archivedConversationsCount = computed(() => {
  const archivedCount = conversationStats.value?.aceleresBoxCounts?.arquivadas;
  if (typeof archivedCount === 'number') {
    return archivedCount;
  }

  return (allConversationsList.value || []).filter(
    conversation =>
      isVisibleConcludedConversationForCurrentUser(conversation) &&
      isArchivedConversation(conversation)
  ).length;
});

const concludedHistoryTabCount = computed(() => {
  return concludedConversationsCount.value;
});

const assigneeTabItems = computed(() => {
  const items = [
    { key: 'novos', name: 'Novos', count: conversationCountForTab('novos') },
    {
      key: wootConstants.ASSIGNEE_TYPE.ME,
      name: 'Meus',
      count: conversationCountForTab(wootConstants.ASSIGNEE_TYPE.ME),
    },
  ];
  if (canSeeOthersBox.value) {
    items.push({
      key: 'outros',
      name: 'Outros',
      count: conversationCountForTab('outros'),
    });
  }
  if (shouldShowPersonalTab.value) {
    items.push({
      key: wootConstants.ASSIGNEE_TYPE.PERSONAL,
      name: 'Pessoais',
      count: conversationCountForTab(wootConstants.ASSIGNEE_TYPE.PERSONAL),
    });
  }
  return items;
});

const quickSearchConversationHeading = computed(() => 'Conversas encontradas');

const supportsUnreadSubfilter = tab => {
  // A subaba Todos/Não lidas também fica disponível em Novos.
  if (tab === 'novos') return true;
  return [
    wootConstants.ASSIGNEE_TYPE.ME,
    'outros',
    wootConstants.ASSIGNEE_TYPE.PERSONAL,
  ].includes(tab);
};

const unreadSubfilterForTab = tab => {
  if (!supportsUnreadSubfilter(tab)) {
    return UNREAD_SUBFILTER_ALL;
  }

  return unreadSubfiltersByTab.value[tab] || UNREAD_SUBFILTER_ALL;
};

const shouldShowUnreadSubfilters = computed(() => {
  return supportsUnreadSubfilter(activeAssigneeTab.value);
});

const activeUnreadSubfilter = computed(() =>
  unreadSubfilterForTab(activeAssigneeTab.value)
);

// Aceleres: o vendedor responsável (ou "Sem vendedor responsável" na fila
// de novos) deve aparecer em todo card de atendimento, não só nas abas
// filtradas/Outros/histórico — sem isso o nome da instância era a única
// informação visível no card, e usuários o confundiam com o vendedor.
// Exceção: na aba Pessoais a conversa já é do próprio vendedor, então o
// nome não agrega informação.
const showAssigneeInConversationCard = computed(() => {
  return activeAssigneeTab.value !== wootConstants.ASSIGNEE_TYPE.PERSONAL;
});

const currentPageFilterKey = computed(() => {
  if (isHistoryTab(activeAssigneeTab.value)) {
    return activeAssigneeTab.value;
  }
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

function conversationStatusForTab(tab) {
  if (isHistoryTab(tab)) {
    return wootConstants.STATUS_TYPE.RESOLVED;
  }

  if (isHistoryTab(activeAssigneeTab.value)) {
    return (
      previousStatusBeforeConcludedTab.value || wootConstants.STATUS_TYPE.OPEN
    );
  }

  return activeStatus.value;
}

function conversationViewCacheKeyForTab(tab) {
  return [
    currentAccountId.value || '',
    currentUser.value?.id || '',
    tab,
    // "Todos" e "Não lidos" são visões diferentes. Manter o subfiltro na
    // chave evita restaurar um snapshot completo quando a tela solicitou
    // somente as conversas não lidas (ou o contrário).
    unreadSubfilterForTab(tab),
    shouldMergeArchivedIntoConcluded.value && tab === CONCLUDED_ASSIGNEE_TAB
      ? 'mobile-history'
      : '',
    conversationStatusForTab(tab),
    activeSortBy.value,
    props.conversationInbox || activeInboxFilter.value || '',
    props.teamId || qfTeamId.value || '',
    props.conversationType || '',
    activeConversationLabel.value || '',
    iaOnly.value ? 'ia' : '',
    qfPriority.value || '',
    qfAgentId.value || '',
  ].join(':');
}

const conversationViewCacheKey = computed(() =>
  conversationViewCacheKeyForTab(currentPageFilterKey.value)
);

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  if (activeAssigneeTab.value === CONCLUDED_ASSIGNEE_TAB) {
    return concludedHistoryTabCount.value;
  }
  if (activeAssigneeTab.value === ARCHIVED_ASSIGNEE_TAB) {
    return archivedConversationsCount.value;
  }
  return conversationTotalCountForTab(activeAssigneeTab.value);
});

const conversationListPagination = computed(() => {
  // A página seguinte depende do estado de paginação da visão atual, não do
  // tamanho da lista anterior. O tamanho podia manter uma visão antiga após
  // trocar filtro/ordenação e fazer a primeira busca nova começar na página 2.
  return (currentFiltersPage.value ?? 0) + 1;
});

const conversationFilters = computed(() => {
  const tab = activeAssigneeTab.value;
  // Mapeia a key da aba p/ o nome da caixa no backend. Cuidado: a aba Meus
  // tem key 'me' (ASSIGNEE_TYPE.ME), não 'meus' — sem isso aceleresBox ficava
  // undefined e Meus não filtrava (mostrava tudo igual a Novos).
  const boxName = aceleresBoxForTab(tab);
  return {
    inboxId: props.conversationInbox
      ? props.conversationInbox
      : activeInboxFilter.value || undefined,
    // Aceleres 4 caixas: o backend filtra por aceleres_box e IGNORA assignee_type
    // quando aceleres_box esta presente. Mantemos assigneeType = key da aba ativa
    // so pro page-tracking (buildConversationList keya a pagina por aqui = currentPageFilterKey).
    // Sem isso a pagina nao avanca e o scroll nao carrega mais (trava na pag 1).
    assigneeType: tab,
    status: isHistoryTab(tab)
      ? wootConstants.STATUS_TYPE.RESOLVED
      : activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: activeConversationLabel.value
      ? [activeConversationLabel.value]
      : undefined,
    teamId: props.teamId || qfTeamId.value || undefined,
    conversationType: props.conversationType || undefined,
    atendidoIa: iaOnly.value || undefined,
    aceleresBox: boxName,
    priority: qfPriority.value || undefined,
    assigneeId: qfAgentId.value || undefined,
    unreadOnly:
      activeUnreadSubfilter.value === UNREAD_SUBFILTER_ONLY || undefined,
    skipCounts: true,
  };
});

const conversationStatsFilters = computed(() => {
  const filters = { ...conversationFilters.value };
  const labelNames = activeLabelFilterNames.value;

  // The stats endpoint already receives aceleres_box. Omitting assigneeType
  // prevents the legacy store guard from discarding labels for Pessoais.
  delete filters.assigneeType;

  if (labelNames.length) {
    filters.labels = labelNames;
  } else {
    delete filters.labels;
  }

  return filters;
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  return 'Painel de atendimento';
});

const isDashboardHomeRoute = computed(() => route.name === 'home');

function filterByAssigneeTab(conversations) {
  const candidates = filterServerValidatedConversations(conversations).filter(
    conversation =>
      !isOperationalAceleresTab(activeAssigneeTab.value) ||
      conversation?.status !== wootConstants.STATUS_TYPE.RESOLVED
  );
  const currentUserId = Number(currentUser.value?.id);
  const assigneeId = conversation =>
    Number(conversation?.meta?.assignee?.id || 0);
  const isAssignedToCurrentUser = conversation =>
    !!currentUserId && assigneeId(conversation) === currentUserId;
  const isUnassigned = conversation => !assigneeId(conversation);
  const isStarted = conversation => isAceleresConversationStarted(conversation);
  const hasPersonalMarker = conversation =>
    hasPersonalConversationMarker(conversation);

  if (activeAssigneeTab.value === 'novos') {
    return candidates.filter(
      c =>
        (isAssignedToCurrentUser(c) &&
          !isStarted(c) &&
          !hasPersonalMarker(c)) ||
        ((isManagerRole.value || isSupervisorUser.value) &&
          isUnassigned(c) &&
          !isStarted(c) &&
          !hasPersonalMarker(c))
    );
  }

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return candidates.filter(
      c =>
        isAssignedToCurrentUser(c) &&
        isStarted(c)
    );
  }

  if (activeAssigneeTab.value === 'outros') {
    // "Outros" é exclusiva de supervisores (supervised_scope no backend).
    // Sem equipe supervisionada, o backend já devolve todos os vendedores
    // sob supervisão direta. Com equipe, restringe aos vendedores das
    // equipes SUPERVISIONADAS — nunca das equipes de gestor.
    const supervisorHasTeamScope = supervisedTeamIds.value.length > 0;

    return candidates.filter(
      c =>
        isSupervisorUser.value &&
        (!supervisorHasTeamScope ||
          supervisedSellerIds.value.includes(assigneeId(c))) &&
        !!assigneeId(c) &&
        !isAssignedToCurrentUser(c) &&
        !hasPersonalMarker(c)
    );
  }

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return candidates.filter(c => isUnassigned(c));
  }

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.PERSONAL) {
    // O backend filtra a caixa, mas o store pode conter itens antigos de outra
    // aba durante a troca. O marcador local impede que conversas de Novos/Meus
    // vazem para Pessoais; inboxes próprias também contam mesmo sem a etiqueta.
    return candidates.filter(c => isPersonalConversationForCurrentUser(c));
  }

  return candidates;
}

const filterByActiveAceleresTab = conversations => {
  const candidates = Array.isArray(conversations) ? conversations : [];

  if (isHistoryTab(activeAssigneeTab.value)) {
    return candidates.filter(isVisibleConversationForHistoryTab);
  }

  return filterByAssigneeTab(candidates);
};

const conversationList = computed(() => {
  if (aceleresViewTransitioning.value) {
    return [];
  }

  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      localConversationList = filterByAssigneeTab(
        participatingChatsList.value(filters)
      );
    } else if (isHistoryTab(activeAssigneeTab.value)) {
      localConversationList = filterByActiveAceleresTab(
        allConversationsList.value
      );
    } else {
      // Aceleres 4 caixas: backend ja filtrou por aceleres_box
      // (novos/meus/outros/pessoais). Mantemos um guard local para evitar
      // vazamento visual quando o store ainda tem itens de outra aba.
      localConversationList = filterByActiveAceleresTab(
        allChatList.value(filters)
      );
    }
  } else {
    // O resultado do filtro avançado vem do servidor já limitado pela caixa,
    // mas pode conter itens de uma resposta ou cache anterior durante a troca
    // de aba. Reaplicar o escopo local mantém a etiqueta como refinamento da
    // aba atual, sem permitir que uma conversa pessoal apareça em Novos.
    localConversationList = filterByActiveAceleresTab(chatLists.value);
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  // O endpoint já aplica `unread_only=true`, e os itens retornados preservam
  // o unread_count mesmo quando pertencem a outro vendedor. O store, porém,
  // mantém a conversa atualmente aberta mesmo fora da página requisitada;
  // removemos esse item extra para a lista visual continuar com exatamente as
  // conversas não lidas retornadas pelo servidor.
  if (activeUnreadSubfilter.value === UNREAD_SUBFILTER_ONLY) {
    localConversationList = localConversationList.filter(
      conversation => Number(conversation?.unread_count || 0) > 0
    );
  }

  return localConversationList;
});

const hasCompleteCachedAceleresView = computed(() => {
  const cacheVersion = aceleresConversationCacheVersion.value;
  return (
    cacheVersion >= 0 &&
    aceleresStartupTabsReady.value &&
    !hasAppliedFiltersOrActiveFolders.value &&
    Object.hasOwn(ACELERES_TAB_BOXES, activeAssigneeTab.value) &&
    aceleresCompleteConversationCacheKeys.has(conversationViewCacheKey.value)
  );
});

const shouldShowChatListLoadingIndicator = computed(() => {
  return (
    aceleresViewTransitioning.value ||
    (chatListLoading.value &&
      !hasCompleteCachedAceleresView.value &&
      !aceleresConversationCache.has(conversationViewCacheKey.value))
  );
});

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !shouldShowChatListLoadingIndicator.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded(conversations) {
  // A resposta descartada por uma troca de aba não pode publicar o estado
  // antigo na lista nem sobrescrever o cache da aba atualmente selecionada.
  if (conversations === undefined) return;

  chatsOnView.value = conversationList.value;
  cacheCurrentAceleresConversationList();
  emit('conversationLoad');
}

function isCacheableAceleresTab() {
  return (
    !hasAppliedFiltersOrActiveFolders.value &&
    Object.hasOwn(ACELERES_TAB_BOXES, activeAssigneeTab.value)
  );
}

function setCachedAceleresConversationList(cacheKey, conversations) {
  aceleresConversationCache.delete(cacheKey);
  aceleresConversationCache.set(cacheKey, [...conversations]);

  if (aceleresConversationCache.size > ACELERES_CONVERSATION_CACHE_LIMIT) {
    const oldestCacheKey = aceleresConversationCache.keys().next().value;
    aceleresConversationCache.delete(oldestCacheKey);
    aceleresCompleteConversationCacheKeys.delete(oldestCacheKey);
  }

  aceleresConversationCacheVersion.value += 1;
}

function isOperationalAceleresCacheKey(cacheKey) {
  const tab = String(cacheKey).split(':')[2];
  return isOperationalAceleresTab(tab);
}

function realtimeMessageSortValue(message) {
  const numericCreatedAt = Number(message?.created_at);
  if (Number.isFinite(numericCreatedAt)) return numericCreatedAt;

  const parsedCreatedAt = Date.parse(message?.created_at || '');
  return Number.isFinite(parsedCreatedAt) ? parsedCreatedAt : 0;
}

function isNewerRealtimeMessage(message, currentMessage) {
  if (!currentMessage) return true;

  const timeDifference =
    realtimeMessageSortValue(message) -
    realtimeMessageSortValue(currentMessage);

  return (
    timeDifference > 0 ||
    (timeDifference === 0 &&
      Number(message?.id || 0) >= Number(currentMessage?.id || 0))
  );
}

function mergeRealtimeMessageIntoConversation(conversation, message) {
  if (!message?.id) return conversation;

  const messages = Array.isArray(conversation.messages)
    ? [...conversation.messages]
    : [];
  const existingMessageIndex = messages.findIndex(
    item => item.id === message.id
  );

  if (existingMessageIndex >= 0) {
    messages.splice(existingMessageIndex, 1, message);
  } else {
    messages.push(message);
  }

  const updates = { messages };
  const isActivity = Number(message.message_type) === MESSAGE_TYPE.ACTIVITY;

  if (!isActivity && !message.private) {
    const currentPreview = conversation.last_non_activity_message;
    if (isNewerRealtimeMessage(message, currentPreview)) {
      updates.last_non_activity_message = message;
    }
  }

  return { ...conversation, ...updates };
}

function synchronizeConversationInAceleresCaches(conversation) {
  const conversationId = Number(conversation?.id);
  if (!conversationId) return;
  const isResolved =
    conversation?.status === wootConstants.STATUS_TYPE.RESOLVED;

  aceleresConversationCache.forEach((cachedConversations, cacheKey) => {
    let conversationFound = false;
    const synchronizedConversations = isResolved
      ? cachedConversations.filter(cachedConversation => {
          const matches = Number(cachedConversation?.id) === conversationId;
          conversationFound ||= matches;
          return !matches || !isOperationalAceleresCacheKey(cacheKey);
        })
      : cachedConversations.map(cachedConversation => {
          if (Number(cachedConversation?.id) !== conversationId) {
            return cachedConversation;
          }

          conversationFound = true;
          const synchronizedConversation = {
            ...cachedConversation,
            ...conversation,
            meta: {
              ...(cachedConversation.meta || {}),
              ...(conversation.meta || {}),
            },
            additional_attributes: {
              ...(cachedConversation.additional_attributes || {}),
              ...(conversation.additional_attributes || {}),
            },
          };

          return mergeRealtimeMessageIntoConversation(
            synchronizedConversation,
            conversation.message
          );
        });

    if (conversationFound) {
      if (isResolved && !synchronizedConversations.length) {
        aceleresConversationCache.delete(cacheKey);
        aceleresCompleteConversationCacheKeys.delete(cacheKey);
      } else {
        aceleresConversationCache.set(cacheKey, synchronizedConversations);
      }
    }
  });

  // Uma conversa concluída não pode voltar para a cache de Meus por meio da
  // regra de sincronização de atribuição. Ela será carregada exclusivamente
  // quando a aba Concluídos for aberta.
  if (isResolved) return;

  if (hasAppliedFiltersOrActiveFolders.value) return;

  const mineTab = wootConstants.ASSIGNEE_TYPE.ME;
  const mineCacheKey = conversationViewCacheKeyForTab(mineTab);
  const mineConversations = aceleresConversationCache.get(mineCacheKey) || [];
  const mineUnreadOnly =
    unreadSubfilterForTab(mineTab) === UNREAD_SUBFILTER_ONLY;
  const belongsToCachedMine =
    !mineUnreadOnly || Number(conversation.unread_count || 0) > 0;

  if (!belongsToCachedMine) {
    aceleresConversationCache.set(
      mineCacheKey,
      mineConversations.filter(cached => Number(cached?.id) !== conversationId)
    );
    return;
  }

  if (
    !mineConversations.some(cached => Number(cached?.id) === conversationId)
  ) {
    setCachedAceleresConversationList(mineCacheKey, [
      conversation,
      ...mineConversations,
    ]);
  }
}

function cacheCurrentAceleresConversationList() {
  // O histórico pode mudar fora do navegador (ex.: uma conversa concluída
  // pelo funil). Não persistir uma fotografia antiga da aba Concluídos.
  if (!isCacheableAceleresTab() || isHistoryTab(activeAssigneeTab.value)) return;

  const cacheKey = conversationViewCacheKey.value;
  const conversations = conversationList.value || [];
  // O store já contém todas as páginas carregadas da visão. Reanexar o cache
  // antigo aqui fazia voltar conversas que o servidor já removeu do filtro.
  setCachedAceleresConversationList(cacheKey, conversations);
}

function startAceleresStartupPrefetch() {
  if (!Reflect.get(window, '__zoraxyLoadingActive')) return false;

  aceleresStartupPrefetchRequests += 1;
  if (aceleresStartupPrefetchRequests === 1) {
    window.startZoraxyConversationPrefetch?.({ targetCount: 1 });
  }

  return true;
}

function finishAceleresStartupPrefetch(wasTracked) {
  if (!wasTracked) return;

  aceleresStartupPrefetchRequests = 0;
  window.finishZoraxyConversationPrefetch?.();
}

// Aceleres: espera o navegador ficar ocioso antes de aquecer as outras abas.
// No boot a lista critica, os stats, o menu e 3 abas disputavam CPU/rede ao
// mesmo tempo — era essa soma que estourava o Rack::Timeout do servidor.
function waitForIdleBeforePrefetch(maxWaitMs = 2000) {
  return new Promise(resolve => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout: maxWaitMs });
      return;
    }
    setTimeout(resolve, 350);
  });
}

async function prefetchAceleresStartupTabs() {
  if (aceleresStartupTabsPromise) return aceleresStartupTabsPromise;

  const tabs = assigneeTabItems.value
    .map(item => item.key)
    .filter(tab => isOperationalAceleresTab(tab));
  if (!tabs.length) {
    aceleresStartupTabsReady.value = true;
    return Promise.resolve();
  }

  const selectedTab = tabs.includes(activeAssigneeTab.value)
    ? activeAssigneeTab.value
    : tabs[0];
  const orderedTabs = [selectedTab, ...tabs.filter(tab => tab !== selectedTab)];

  const warmStartupTab = async tab => {
    const cacheKey = conversationViewCacheKeyForTab(tab);
    // A aba atual já foi buscada pela requisição crítica. Reutilizá-la
    // evita repetir imediatamente a mesma consulta ao entrar no inbox.
    const conversations =
      tab === selectedTab
        ? [...(conversationList.value || [])]
        : await store.dispatch('prefetchConversationList', {
            ...conversationFilters.value,
            assigneeType: tab,
            aceleresBox: ACELERES_TAB_BOXES[tab],
            // Não herdar o subfiltro "Não lidas" ao aquecer outra aba.
            unreadOnly: undefined,
            // Contadores são carregados separadamente e em segundo plano.
            skipCounts: true,
            page: 1,
          });

    const tabConversations = Array.isArray(conversations)
      ? conversations
      : [];
    setCachedAceleresConversationList(cacheKey, tabConversations);

    if (tab !== selectedTab) return;

    // Somente a aba visível recebe mensagens no boot. As demais carregam
    // suas mensagens quando o usuário as abrir, sem disputar CPU/rede.
    const messageTargets = tabConversations.slice(
      0,
      ACELERES_STARTUP_MESSAGE_CONVERSATIONS
    );
    if (!messageTargets.length) return;

    const conversationsWithMessages = await store.dispatch(
      'prefetchConversationMessages',
      {
        conversations: messageTargets,
        messageLimit: ACELERES_STARTUP_MESSAGE_LIMIT,
      }
    );
    const messagesById = new Map(
      conversationsWithMessages.map(conversation => [
        Number(conversation.id),
        conversation,
      ])
    );
    const hydratedConversations = tabConversations.map(
      conversation =>
        messagesById.get(Number(conversation.id)) || conversation
    );

    setCachedAceleresConversationList(cacheKey, hydratedConversations);
    await store.dispatch('mergeConversationPage', hydratedConversations);
  };

  aceleresStartupTabsPromise = orderedTabs
    .reduce(
      (promise, tab, index) =>
        promise.then(async () => {
          await warmStartupTab(tab);
          if (index < orderedTabs.length - 1) {
            await new Promise(resolve => {
              setTimeout(resolve, 350);
            });
          }
        }),
      waitForIdleBeforePrefetch()
    )
    .then(() => {
      aceleresStartupTabsReady.value = true;
    })
    .catch(error => {
      aceleresStartupTabsPromise = null;
      throw error;
    });

  return aceleresStartupTabsPromise;
}

function ensureAceleresCriticalMenuData() {
  if (aceleresCriticalMenuDataPromise) {
    return aceleresCriticalMenuDataPromise;
  }

  aceleresCriticalMenuDataPromise = Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
    store.dispatch('labels/get'),
    store.dispatch('inboxes/get'),
    store.dispatch('customViews/get', 'conversation'),
    store.dispatch('customViews/get', 'contact'),
  ]).catch(error => {
    aceleresCriticalMenuDataPromise = null;
    throw error;
  });

  return aceleresCriticalMenuDataPromise;
}

function restoreCachedAceleresConversationList() {
  // Sempre recarrega Concluídos/Arquivadas do servidor. Essas abas recebem
  // mudanças de status vindas do funil e um cache de sessão pode esconder uma
  // conversa recém-concluída.
  if (!isCacheableAceleresTab() || isHistoryTab(activeAssigneeTab.value)) return false;

  const cachedConversations = aceleresConversationCache.get(
    conversationViewCacheKey.value
  );
  // Cache vazio nao e pre-carga: pode ter sido gravado durante uma troca de
  // aba enquanto a resposta anterior ainda estava chegando. Nesse caso a
  // caixa ativa precisa consultar o servidor novamente.
  if (!Array.isArray(cachedConversations) || !cachedConversations.length) {
    aceleresConversationCache.delete(conversationViewCacheKey.value);
    aceleresCompleteConversationCacheKeys.delete(
      conversationViewCacheKey.value
    );
    return false;
  }

  const selectedConversation = currentChat.value?.id ? currentChat.value : null;
  const selectedConversationIsCached = cachedConversations.some(
    conversation =>
      Number(conversation?.id) === Number(selectedConversation?.id)
  );
  const conversationsForStore =
    selectedConversation && !selectedConversationIsCached
      ? [...cachedConversations, selectedConversation]
      : cachedConversations;

  store.dispatch('mergeConversationPage', conversationsForStore);
  chatsOnView.value = cachedConversations;
  return true;
}

function resetAceleresConversationListState({
  resetPagination = true,
  clearConversationStore = false,
} = {}) {
  aceleresLastLoadMoreKey.value = null;
  // Nas trocas de aba, a lista anterior não representa a nova visão. Limpar o
  // store evita mostrar uma mistura transitória enquanto a primeira página é
  // consultada; nos demais refreshes mantemos o comportamento sem flicker.
  if (clearConversationStore) {
    store.dispatch('emptyAllConversations', {
      preserveSelectedConversation: true,
    });
    chatsOnView.value = [];
  } else {
    chatsOnView.value = conversationList.value;
  }
  if (conversationListRef.value) {
    conversationListRef.value.scrollTop = 0;
  }
  if (resetPagination) {
    store.dispatch('conversationPage/reset');
  }
}

function buildConversationStatsCacheKey(params = {}) {
  return JSON.stringify({
    inboxId: params.inboxId || '',
    status: params.status || '',
    teamId: params.teamId || '',
    conversationType: params.conversationType || '',
    labels: Array.isArray(params.labels) ? [...params.labels].sort() : [],
    priority: params.priority || '',
    assigneeId: params.assigneeId || '',
    atendidoIa: params.atendidoIa || '',
  });
}

function fetchConversationStats({ immediate = false, force = false } = {}) {
  const statsPayload = { ...conversationStatsFilters.value };

  const nextMetaKey = buildConversationStatsCacheKey(statsPayload);
  const statsUpdatedAt = new Date(
    conversationStats.value?.updatedOn || 0
  ).getTime();
  const hasFreshStats =
    statsUpdatedAt > 0 &&
    Date.now() - statsUpdatedAt < ACELERES_STATS_CACHE_TTL &&
    conversationStats.value?.metaKey === nextMetaKey;

  if (!immediate && !force && hasFreshStats) {
    return Promise.resolve();
  }

  return store.dispatch('conversationStats/get', {
    ...statsPayload,
    immediate,
    skip_cache: immediate,
  });
}

let realtimeStatsRefreshTimer = null;

function scheduleRealtimeStatsRefresh() {
  if (realtimeStatsRefreshTimer) {
    clearTimeout(realtimeStatsRefreshTimer);
  }

  realtimeStatsRefreshTimer = setTimeout(() => {
    realtimeStatsRefreshTimer = null;
    fetchConversationStats({ force: true, immediate: true });
  }, 250);
}

function applyAceleresBoxCountDelta(delta = {}) {
  const currentCounts = conversationStats.value?.aceleresBoxCounts || {};
  const nextCounts = [
    'novos',
    'meus',
    'outros',
    'pessoais',
    'concluidos',
    'arquivadas',
  ].reduce((counts, key) => {
    const currentValue = Number(currentCounts[key] || 0);
    const nextValue = currentValue + Number(delta[key] || 0);
    counts[key] = Math.max(0, nextValue);
    return counts;
  }, {});

  store.dispatch('conversationStats/set', {
    aceleres_box_counts: nextCounts,
  });
}

function optimisticUnreadBoxForConversation(conversation) {
  if (!conversation || conversation.status === wootConstants.STATUS_TYPE.RESOLVED) {
    return null;
  }

  if (isPersonalConversationForCurrentUser(conversation)) return 'pessoais';

  const currentUserId = Number(currentUser.value?.id || 0);
  const assigneeId = Number(
    conversation?.meta?.assignee?.id || conversation?.assignee_id || 0
  );
  const started = isAceleresConversationStarted(conversation);

  if (assigneeId === currentUserId && currentUserId > 0) {
    return started ? 'meus' : 'novos';
  }

  if (!assigneeId && (isManagerRole.value || isSupervisorUser.value) && !started) {
    return 'novos';
  }

  if (
    assigneeId &&
    assigneeId !== currentUserId &&
    isSupervisorUser.value &&
    (!supervisedTeamIds.value.length || supervisedSellerIds.value.includes(assigneeId))
  ) {
    return 'outros';
  }

  return null;
}

function adjustUnreadCountOptimistically({
  conversationId,
  previousUnreadCount,
  unreadCount,
  message,
}) {
  if (
    !conversationStats.value?.aceleresBoxUnreadCounts ||
    (message &&
      (message.private === true || Number(message.message_type) !== 0))
  ) {
    return;
  }

  const conversation = getConversationById.value(conversationId);
  const box = optimisticUnreadBoxForConversation(conversation);
  if (!box) return;

  const wasUnread = Number(previousUnreadCount || 0) > 0;
  const isUnread = Number(unreadCount || 0) > 0;
  const delta = Number(isUnread) - Number(wasUnread);
  if (!delta) return;

  store.dispatch('conversationStats/adjustUnreadCounts', { box, delta });
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  return store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
      sortBy: activeSortBy.value,
      aceleresBox: aceleresBoxForTab(activeAssigneeTab.value),
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  return store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
      sortBy: activeSortBy.value,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  resetAceleresConversationListState();
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

// Aceleres: retry manual quando a lista falhou (listErrorStatus). Forca busca
// fresca ignorando o cache local, para o usuario nao depender de F5.
function retryFetchConversations() {
  return Promise.resolve(
    fetchConversations({ bypassCache: true, waitForStats: false })
  ).catch(() => null);
}

function fetchConversations({
  immediateStats = false,
  bypassCache = false,
  waitForStats = true,
} = {}) {
  const canUseAceleresCache =
    !Reflect.get(window, '__zoraxyLoadingActive') &&
    !hasAppliedFiltersOrActiveFolders.value &&
    !bypassCache;
  const restoredFromCache = canUseAceleresCache
    ? restoreCachedAceleresConversationList()
    : false;

  if (restoredFromCache) {
    // O snapshot aparece imediatamente; uma atualização eventual não deve
    // manter a transição visual aberta nem bloquear a troca de aba.
    aceleresViewTransitioning.value = false;
    const startupTabsRequest = aceleresStartupTabsReady.value
      ? Promise.resolve()
      : prefetchAceleresStartupTabs();
    // Keep warming the other tabs, but do not hold the transition open until
    // all of their message requests finish.
    startupTabsRequest.catch(() => null);
    if (
      !aceleresCompleteConversationCacheKeys.has(
        conversationViewCacheKey.value
      )
    ) {
      const refreshRequest = refreshConversationListInBackground({
        force: true,
      });
      if (!waitForStats) {
        refreshRequest.catch(() => null);
        return Promise.resolve();
      }
      return refreshRequest;
    }
    const statsRequest = fetchConversationStats({ immediate: immediateStats });
    if (!waitForStats) {
      statsRequest.catch(() => null);
      return Promise.resolve();
    }
    return statsRequest;
  }

  const tracksStartupPrefetch = startAceleresStartupPrefetch();
  store.dispatch('updateChatListFilters', conversationFilters.value);
  const statsRequest = fetchConversationStats({ immediate: immediateStats });
  const menuDataRequest = ensureAceleresCriticalMenuData();
  const conversationRequest = store
    .dispatch('fetchAllConversations')
    .then(emitConversationLoaded);
  const startupTabsRequest = Promise.all([conversationRequest, menuDataRequest]).then(
    () => prefetchAceleresStartupTabs()
  );
  // Secondary tabs are a background warm-up. The current tab becomes
  // interactive as soon as its own list and critical menu data are ready.
  startupTabsRequest.catch(() => null);
  return waitForZoraxyCriticalData({
    conversations: conversationRequest,
    stats: waitForStats ? statsRequest : Promise.resolve(),
    menuData: menuDataRequest,
  }).finally(() => {
    finishAceleresStartupPrefetch(tracksStartupPrefetch);
  });
}

async function preloadMessagesForVisibleAceleresView(viewKey) {
  if (viewKey !== conversationViewCacheKey.value) return;

  const targets = (conversationList.value || [])
    .filter(conversation => !conversation?.dataFetched)
    .slice(0, ACELERES_STARTUP_MESSAGE_CONVERSATIONS);
  if (!targets.length) return;

  try {
    const hydratedConversations = await store.dispatch(
      'prefetchConversationMessages',
      {
        conversations: targets,
        messageLimit: ACELERES_STARTUP_MESSAGE_LIMIT,
      }
    );
    if (viewKey !== conversationViewCacheKey.value) return;

    const hydratedById = new Map(
      hydratedConversations.map(conversation => [
        Number(conversation.id),
        conversation,
      ])
    );
    const mergedConversations = (conversationList.value || []).map(
      conversation =>
        hydratedById.get(Number(conversation.id)) || conversation
    );
    await store.dispatch('mergeConversationPage', mergedConversations);
    setCachedAceleresConversationList(viewKey, mergedConversations);
    chatsOnView.value = mergedConversations;
  } catch {
    // A lista continua utilizável; a abertura da conversa faz o carregamento completo.
  }
}

function fetchAceleresView(options = {}) {
  const requestedViewKey = conversationViewCacheKey.value;
  aceleresViewTransitioning.value = true;

  return Promise.resolve(fetchConversations(options)).finally(async () => {
    if (requestedViewKey === conversationViewCacheKey.value) {
      aceleresViewTransitioning.value = false;
      await preloadMessagesForVisibleAceleresView(requestedViewKey);
    }
  });
}

const autoRefreshInFlight = ref(false);
let autoRefreshQueued = false;
let realtimeConversationRefreshTimer = null;

function scheduleRealtimeConversationListRefresh() {
  if (realtimeConversationRefreshTimer) {
    clearTimeout(realtimeConversationRefreshTimer);
  }

  realtimeConversationRefreshTimer = setTimeout(() => {
    realtimeConversationRefreshTimer = null;
    refreshConversationListInBackground({
      force: false,
      refreshStats: false,
    }).catch(() => {});
  }, 250);
}

async function refreshConversationListInBackground({
  force = false,
  refreshStats = true,
} = {}) {
  if (autoRefreshInFlight.value) {
    autoRefreshQueued = true;
    return;
  }

  if (
    !shouldRunChatListAutoRefresh({
      hasActiveFolders: hasActiveFolders.value,
      hasAppliedFilters: hasAppliedFilters.value,
      isLoading: force ? false : chatListLoading.value,
      isRefreshing: false,
    })
  ) {
    return;
  }

  autoRefreshInFlight.value = true;
  try {
    const requestedViewKey = conversationViewCacheKey.value;
    const requestedPageFilterKey = currentPageFilterKey.value;
    const requestedFilters = {
      ...conversationFilters.value,
      page: 1,
    };
    store.dispatch('updateChatListFilters', requestedFilters);
    const [conversations] = await Promise.all([
      store.dispatch('prefetchConversationList', requestedFilters),
      refreshStats
        ? fetchConversationStats({ immediate: force })
        : Promise.resolve(),
    ]);

    if (requestedViewKey === conversationViewCacheKey.value) {
      if (force) {
        // Refresh autoritativo: a página nova define a visão atual. Mesclar
        // itens antigos reintroduzia conversas fora do filtro/caixa e causava
        // o efeito de sumir e reaparecer entre recargas.
        if (!Array.isArray(conversations)) {
          return;
        }
        const synchronizedConversations = Array.isArray(conversations)
          ? conversations
          : [];
        setCachedAceleresConversationList(
          requestedViewKey,
          synchronizedConversations
        );
        // O refresh busca somente a primeira página; a visão precisa voltar a
        // permitir paginação, mesmo que o cache anterior estivesse completo.
        aceleresCompleteConversationCacheKeys.delete(requestedViewKey);
        await store.dispatch(
          'replaceConversationPage',
          synchronizedConversations
        );
        await store.dispatch('conversationPage/reset');
        await store.dispatch('conversationPage/setCurrentPage', {
          filter: requestedPageFilterKey,
          page: 1,
        });
        if (
          !aceleresCompleteConversationCacheKeys.has(requestedViewKey) &&
          synchronizedConversations.length < ACELERES_CONVERSATIONS_PER_PAGE
        ) {
          await store.dispatch('conversationPage/setEndReached', {
            filter: requestedPageFilterKey,
          });
        }
      } else {
        await store.dispatch('mergeConversationPage', conversations);
      }
      chatsOnView.value = conversationList.value;
      await preloadMessagesForVisibleAceleresView(requestedViewKey);
      cacheCurrentAceleresConversationList();
    }
  } finally {
    autoRefreshInFlight.value = false;
    if (autoRefreshQueued) {
      autoRefreshQueued = false;
      refreshConversationListInBackground({ force: false }).catch(() => {});
    }
  }
}

function resetAndFetchData({ bypassCache = false } = {}) {
  aceleresFilterApplied.value = false;
  appliedIaFilter.value = false;
  aceleresDropdownFilterRef.value?.clearAll(false);
  appliedFilter.value = [];
  resetBulkActions();
  resetAceleresConversationListState();
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations({
    immediateStats: true,
    bypassCache,
    // A lista deve ficar interativa assim que a primeira página responder;
    // os contadores continuam sendo atualizados pelo endpoint /meta.
    waitForStats: false,
  });
}

// Aceleres: o IntersectionObserver do sentinel remonta a cada toggle do spinner
// e pode redisparar em rajada. A trava por chave abaixo cobre so o caminho sem
// filtro; este throttle protege tambem os caminhos de pasta/filtro salvo.
let aceleresLastLoadMoreAt = 0;
const LOAD_MORE_THROTTLE_MS = 300;

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  const now = Date.now();
  if (now - aceleresLastLoadMoreAt < LOAD_MORE_THROTTLE_MS) return;
  aceleresLastLoadMoreAt = now;

  if (!hasAppliedFiltersOrActiveFolders.value) {
    if (hasCompleteCachedAceleresView.value) {
      return;
    }

    if (
      activeAssigneeTabCount.value > 0 &&
      conversationList.value.length >= activeAssigneeTabCount.value
    ) {
      return;
    }

    // Aceleres: trava anti-loop. O sentinel (IntersectionObserver) remonta a
    // cada toggle do spinner e redispara o observed; quando a lista local fica
    // vazia (filtro client-side) a pagina pedida nunca avanca e o payload nunca
    // vem vazio, entao sem esta trava o fetch se repete indefinidamente.
    const key = JSON.stringify([
      conversationFilters.value,
      conversationList.value.length,
    ]);
    if (key === aceleresLastLoadMoreKey.value) return;
    aceleresLastLoadMoreKey.value = key;
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Use IntersectionObserver instead of @scroll since Virtualizer only emits on user scroll.
// If the list doesn’t fill the viewport, loading can stall.
// IntersectionObserver triggers as soon as the sentinel is visible.
const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

function updateAssigneeTab(
  selectedTab,
  { updateRouteQuery = true, fetchData = true } = {}
) {
  if (
    selectedTab === wootConstants.ASSIGNEE_TYPE.PERSONAL &&
    !shouldShowPersonalTab.value
  ) {
    return updateAssigneeTab('novos', { updateRouteQuery });
  }

  if (activeAssigneeTab.value !== selectedTab) {
    if (
      !aceleresCompleteConversationCacheKeys.has(conversationViewCacheKey.value)
    ) {
      cacheCurrentAceleresConversationList();
    }
    aceleresUserPickedTab.value = true;
    resetBulkActions();
    emitter.emit('clearSearchInput');
    if (isHistoryTab(selectedTab) && !isHistoryTab(activeAssigneeTab.value)) {
      previousStatusBeforeConcludedTab.value = activeStatus.value;
      activeStatus.value = wootConstants.STATUS_TYPE.RESOLVED;
    } else if (
      !isHistoryTab(selectedTab) &&
      isHistoryTab(activeAssigneeTab.value)
    ) {
      activeStatus.value =
        previousStatusBeforeConcludedTab.value ||
        wootConstants.STATUS_TYPE.OPEN;
    }
    activeAssigneeTab.value = selectedTab;
    if (updateRouteQuery) {
      setAceleresBoxQuery(selectedTab);
    }
    // Trocar de aba precisa mostrar uma única visão atualizada. Não restaurar
    // um snapshot antigo evita o efeito de carregar uma lista e substituí-la
    // logo depois por outra resposta.
    resetAceleresConversationListState({
      resetPagination: true,
      clearConversationStore: false,
    });
    if (!fetchData) {
      return Promise.resolve();
    }

    return fetchAceleresView({
      immediateStats: false,
      bypassCache: false,
      // Os contadores continuam atualizando em segundo plano; a troca de
      // aba não deve aguardar o endpoint de meta para mostrar a lista.
      waitForStats: false,
    });
  }

  if (updateRouteQuery) {
    setAceleresBoxQuery(selectedTab);
  }

  return Promise.resolve();
}

async function showMineTabAfterConversationStart({
  conversationId,
  refreshConversation = true,
  conversation = null,
} = {}) {
  let conversationToKeep = conversation;
  if (conversationId && refreshConversation) {
    conversationToKeep =
      (await store.dispatch('getConversation', conversationId)) ||
      conversationToKeep;
  }

  const keepStartedConversationVisible = () => {
    if (!conversationToKeep) return;
    store.dispatch('addConversation', conversationToKeep);
    store.dispatch('updateConversation', conversationToKeep);
  };

  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    resetAceleresConversationListState();
    await fetchConversations({ immediateStats: true });
    keepStartedConversationVisible();
    return;
  }

  await updateAssigneeTab(wootConstants.ASSIGNEE_TYPE.ME, {
    immediateStats: true,
    updateRouteQuery: false,
  });
  keepStartedConversationVisible();
}

function updateUnreadSubfilter(nextValue) {
  if (!shouldShowUnreadSubfilters.value) {
    return;
  }

  if (activeUnreadSubfilter.value === nextValue) {
    return;
  }

  const previousCacheKey = conversationViewCacheKey.value;

  unreadSubfiltersByTab.value = {
    ...unreadSubfiltersByTab.value,
    [activeAssigneeTab.value]: nextValue,
  };

  const nextCacheKey = conversationViewCacheKey.value;
  [previousCacheKey, nextCacheKey].forEach(cacheKey => {
    aceleresConversationCache.delete(cacheKey);
    aceleresCompleteConversationCacheKeys.delete(cacheKey);
  });
  aceleresConversationCacheVersion.value += 1;

  resetBulkActions();
  resetAceleresConversationListState({ clearConversationStore: true });

  // A troca de subfiltro precisa consultar a primeira página do filtro novo.
  // Apenas recalcular a lista local deixa conversas fora do snapshot atual e
  // fazia o sentinel declarar "todas carregadas" prematuramente.
  return fetchAceleresView({ immediateStats: true, bypassCache: true });
}

function onSortChange(value) {
  if (activeSortBy.value === value) return;

  activeSortBy.value = value;
  store.dispatch('setChatSortFilter', value);
  resetBulkActions();
  resetAceleresConversationListState();

  if (hasActiveFolders.value) {
    fetchSavedFilteredConversations(activeFolder.value.query);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  } else {
    fetchConversations();
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else if (type === 'inbox') {
    activeInboxFilter.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function onInboxFilterChange(inboxId) {
  activeInboxFilter.value = inboxId;
  resetAndFetchData();
}

function onAceleresFilterApply(conditions) {
  if (!conditions || conditions.length === 0) {
    onAceleresFilterClear();
    return;
  }
  aceleresFilterApplied.value = true;
  activeAceleresFilterConditions.value = conditions;
  appliedIaFilter.value = conditions.some(
    c => c.attributeKey === 'atendido_ia' || c.attribute_key === 'atendido_ia'
  );
  const snaked = useSnakeCase(JSON.parse(JSON.stringify(conditions)));
  store.dispatch('setConversationFilters', snaked);
  resetBulkActions();
  resetAceleresConversationListState();
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(snaked),
      page: 1,
      sortBy: activeSortBy.value,
      aceleresBox: aceleresBoxForTab(activeAssigneeTab.value),
    })
    .then(emitConversationLoaded);
  fetchConversationStats();
}

function onAceleresFilterClear() {
  activeAceleresFilterConditions.value = [];
  resetAndFetchData();
}

function toggleIaFilter() {
  iaOnly.value = !iaOnly.value;
  resetAndFetchData();
}

function onQfStatusChange(status) {
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  store.dispatch('setChatStatusFilter', activeStatus.value);
  resetAndFetchData();
}

function onQfLabelChange(labelSlug) {
  qfLabelSlug.value = labelSlug;
  resetAndFetchData();
}

function onQfTeamChange(teamId) {
  qfTeamId.value = teamId;
  resetAndFetchData();
}

function onQfPriorityChange(priority) {
  qfPriority.value = priority;
  resetAndFetchData();
}

function onQfAgentChange(agentId) {
  qfAgentId.value = agentId;
  resetAndFetchData();
}

function clearQfFilters() {
  activeInboxFilter.value = null;
  activeStatus.value = wootConstants.STATUS_TYPE.OPEN;
  store.dispatch('setChatStatusFilter', activeStatus.value);
  qfPriority.value = null;
  qfLabelSlug.value = null;
  qfAgentId.value = null;
  qfTeamId.value = null;
  resetAndFetchData();
}
function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

async function onAssignPersonal(conversationId) {
  applyAceleresBoxCountDelta({ meus: -1, pessoais: 1 });
  try {
    await store.dispatch('assignAgent', {
      conversationId,
      agentId: null,
      personal: true,
    });
  } catch (error) {
    applyAceleresBoxCountDelta({ meus: 1, pessoais: -1 });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.PERSONAL_ASSIGNMENT.FAILED')
    );
  }
}

async function onStartConversation(conversationId) {
  try {
    const response = await AceleresTeamPresenceAPI.lockConversation({
      conversationDisplayId: conversationId,
    });
    const conversation = getConversationById.value(conversationId);
    const currentUserData = currentUser.value || {};
    const assignee = response?.data?.assignee || {
      id: currentUserData.id,
      name: currentUserData.name,
      available_name: currentUserData.available_name,
      email: currentUserData.email,
      thumbnail: currentUserData.thumbnail,
    };
    let startedConversation = null;

    if (conversation) {
      const currentUpdatedAt = Number(conversation.updated_at) || 0;
      startedConversation = {
        ...conversation,
        updated_at: Math.max(
          currentUpdatedAt + 1,
          Math.floor(Date.now() / 1000)
        ),
        meta: {
          ...(conversation.meta || {}),
          assignee,
        },
        additional_attributes: {
          ...(conversation.additional_attributes || {}),
          ...(response?.data?.additional_attributes || {}),
          rodizio_locked: true,
          attendance_started: true,
          rodizio_locked_by: currentUserData.id,
          rodizio_locked_reason: 'iniciar_button',
          rodizio_locked_at: new Date().toISOString(),
        },
      };
      await store.dispatch('updateConversation', startedConversation);
    }

    const fetchedConversation = await store.dispatch(
      'getConversation',
      conversationId
    );
    synchronizeConversationInAceleresCaches(
      fetchedConversation || startedConversation
    );
    applyAceleresBoxCountDelta({ novos: -1, meus: 1 });
    await showMineTabAfterConversationStart({
      conversationId,
      refreshConversation: false,
      conversation: fetchedConversation || startedConversation,
    });
    useAlert('Atendimento iniciado e movido para Meus.');
  } catch (error) {
    useAlert('Não foi possível iniciar o atendimento. Tente novamente.');
  }
}

async function onAssignMine(conversationId) {
  const isPersonalConversation = shouldRouteUnassignToPersonal(conversationId);
  applyAceleresBoxCountDelta(
    isPersonalConversation ? { novos: 1, pessoais: -1 } : { meus: 1, pessoais: -1 }
  );
  try {
    if (isPersonalConversation) {
      await store.dispatch('assignAgent', {
        conversationId,
        agentId: currentUser.value?.id,
        start: false,
      });
      const assignedConversation = await store.dispatch(
        'getConversation',
        conversationId
      );
      await updateAssigneeTab('novos');
      synchronizeConversationInAceleresCaches(assignedConversation);
      useAlert('Contato atribuído a você e movido para Novos.');
      return;
    }

    const movedConversation = await store.dispatch('moveConversationToMine', {
      conversationId,
    });
    await showMineTabAfterConversationStart({
      conversationId,
      refreshConversation: false,
      conversation: movedConversation,
    });
  } catch (error) {
    applyAceleresBoxCountDelta(
      isPersonalConversation ? { novos: -1, pessoais: 1 } : { meus: -1, pessoais: 1 }
    );
    useAlert(
      isPersonalConversation
        ? 'Não foi possível atribuir o contato para Novos. Tente novamente.'
        : 'Não foi possível mover a conversa para meus. Tente novamente.'
    );
  }
}

async function onAssignSelf(conversationId) {
  try {
    await store.dispatch('assignAgent', {
      conversationId,
      agentId: currentUser.value?.id,
      start: false,
    });
    await store.dispatch('getConversation', conversationId);
    useAlert('Lead atribuído a você e mantido em Novos.');
  } catch (error) {
    useAlert('Não foi possível atribuir o lead a você. Tente novamente.');
  }
}

function shouldRouteUnassignToPersonal(conversationId) {
  const conversation = getConversationById.value(conversationId);
  if (!conversation) {
    return false;
  }

  const labels = Array.isArray(conversation.labels) ? conversation.labels : [];
  const currentUserId = Number(currentUser.value?.id);
  const personalOwnerId = Number(
    conversation.additional_attributes?.personal_owner_chatwoot_user_id
  );
  const isOwnedPersonalInbox = ownedPersonalInboxIds.value.includes(
    Number(conversation.inbox_id)
  );

  return (
    isOwnedPersonalInbox ||
    personalOwnerId === currentUserId ||
    labels.includes('pessoal')
  );
}

async function onAssignAgent(agent, conversationIds = null) {
  const ids = Array.isArray(conversationIds)
    ? conversationIds
    : conversationIds
      ? [conversationIds]
      : [];

  if (
    agent?.id == null &&
    ids.length === 1 &&
    shouldRouteUnassignToPersonal(ids[0])
  ) {
    await onAssignPersonal(ids[0]);
    return;
  }

  if (
    Number(agent?.id) === Number(currentUser.value?.id) &&
    ids.length === 1 &&
    shouldRouteUnassignToPersonal(ids[0])
  ) {
    await onAssignMine(ids[0]);
    return;
  }

  await bulkAssignAgent(agent, conversationIds);
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter(
  'fetch_conversation_stats',
  ({ force = true, immediate = false } = {}) => {
    if (
      hasActiveFolders.value ||
      (hasAppliedFilters.value && !aceleresFilterApplied.value)
    )
      return;
    // A leitura continua imediata; mensagens novas usam a atualização local
    // do WebSocket e uma consulta de meta com debounce para não bloquear a
    // fila de chegada em períodos de alto volume.
    if (immediate) {
      fetchConversationStats({ force, immediate });
      return;
    }

    scheduleRealtimeStatsRefresh();
  }
);

useEmitter(
  'conversation_realtime_message',
  ({
    conversationId,
    lastActivityAt,
    unreadCount,
    message,
    previousUnreadCount,
  } = {}) => {
    const normalizedConversationId = Number(conversationId);
    if (!normalizedConversationId) return;

    // Atualiza snapshots já aquecidos para que uma troca de caixa não volte a
    // mostrar a versão anterior da conversa.
    synchronizeConversationInAceleresCaches({
      id: normalizedConversationId,
      last_activity_at: lastActivityAt,
      timestamp: lastActivityAt,
      unread_count: unreadCount,
      message,
    });
    adjustUnreadCountOptimistically({
      conversationId: normalizedConversationId,
      previousUnreadCount,
      unreadCount,
      message,
    });

    // Mensagens, preview, unread e ordenação já foram atualizados localmente
    // pelo store quando a conversa já está visível. A existência no store não
    // basta: ela pode pertencer a outra aba/página e ainda não estar na lista
    // atual. Nesse caso, o refresh busca a conversa e reposiciona a primeira
    // página para que ela apareça.
    const isConversationInVisibleList = conversationList.value.some(
      conversation => Number(conversation?.id) === normalizedConversationId
    );
    if (!isConversationInVisibleList) {
      scheduleRealtimeConversationListRefresh();
    }
    // A nova mensagem também altera o total de conversas não lidas da caixa.
    // A reconciliação é agrupada para não iniciar uma consulta pesada por
    // mensagem e não competir com a entrega do próprio evento.
    scheduleRealtimeStatsRefresh();
  }
);

// A leitura local emite conversation_unread_state_changed e, logo depois,
// fetch_conversation_stats. O contador pendente anterior consumia o segundo
// evento e impedia a atualização dos badges do menu.
useEmitter(
  'conversation_unread_state_changed',
  ({ id, previousUnreadCount, unreadCount } = {}) => {
    adjustUnreadCountOptimistically({
      conversationId: id,
      previousUnreadCount,
      unreadCount,
    });
  }
);

useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, () => {
  refreshConversationListInBackground({ force: true });
});

// O merge otimista de mensagens realtime nesta cache (session storage) roda
// fora do Vuex e pode divergir do que o backend consideraria visível para o
// agente (Aceleres::ConversationHistoryScope). Quando o store reconcilia a
// preview com a versão autoritativa (store/modules/conversations/actions.js
// #reconcileConversationPreview), aplicamos a mesma correção aqui para que a
// cache de sessão não reintroduza o preview desatualizado ao trocar de aba.
useEmitter(
  BUS_EVENTS.ACELERES_CONVERSATION_PREVIEW_RECONCILED,
  conversation => {
    synchronizeConversationInAceleresCaches(conversation);
  }
);

useEmitter(
  BUS_EVENTS.ACELERES_CONVERSATION_STARTED,
  ({ conversationId } = {}) => {
    showMineTabAfterConversationStart({ conversationId });
  }
);

onMounted(() => {
  // A rota pode iniciar em Meus. Apenas sincroniza o estado aqui; a carga
  // única da lista/contadores acontece em resetAndFetchData abaixo.
  syncAssigneeTabFromRoute({ fetchData: false });
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  // Os dados dos menus entram na mesma barreira curta da lista e contadores.
  ensureAceleresCriticalMenuData();
  // Aceleres: ao entrar no Zoraxy, a lista deve abrir nas caixas globais
  // (Novos/Meus) sem reaplicar filtro salvo de uma sessao anterior. Filtro salvo
  // podia manter inbox/condicoes antigas e zerar os badges mesmo com conversas.
  aceleresDropdownFilterRef.value?.clearAll(false);
  resetAndFetchData({ bypassCache: true });
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
  emitter.on(BUS_EVENTS.MESSAGE_SENT, closeQuickSearch);
  window.addEventListener(
    EMPTY_STATE_NEW_CONTACT_EVENT,
    openNewContactFromEmptyState
  );
  // Aceleres: funil abre conversa com ?qs=NomeDaContato -> auto-popula busca
  const qsParam = route.query.qs;
  if (qsParam && qsParam.trim().length >= 2) {
    quickSearch.value = qsParam.trim();
    onQuickSearchInput();
    const newQuery = Object.assign({}, route.query);
    delete newQuery.qs;
    router.replace({ query: newQuery });
  }
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.MESSAGE_SENT, closeQuickSearch);
  window.removeEventListener(
    EMPTY_STATE_NEW_CONTACT_EVENT,
    openNewContactFromEmptyState
  );
  autoRefreshQueued = false;
  if (realtimeStatsRefreshTimer) {
    clearTimeout(realtimeStatsRefreshTimer);
    realtimeStatsRefreshTimer = null;
  }
});

const selectedConversationId = ref(null);
const showDeleteConversationPopup = ref(false);
const isDeletingConversation = ref(false);
const conversationDeleteModalText = {
  cancel: 'Cancelar',
  closeAriaLabel: 'Cancelar exclusão',
  confirm: 'Apagar',
  confirming: 'Excluindo...',
};

const selectedConversationForDelete = computed(() =>
  conversationList.value.find(
    conversation =>
      Number(conversation?.id) === Number(selectedConversationId.value)
  )
);

const selectedConversationDeleteTitle = computed(() => {
  const conversation = selectedConversationForDelete.value;
  const contact = conversation?.contact || conversation?.meta?.sender || {};
  const contactLabel =
    contact.phone_number || contact.phoneNumber || contact.name || '';

  return contactLabel
    ? `Deseja apagar a conversa com ${contactLabel}?`
    : 'Deseja apagar a conversa?';
});

const selectedConversationDeleteDescription = computed(
  () => 'As mensagens serão removidas de todos os dispositivos.'
);

const canDeleteSelectedConversation = computed(
  () => !isDeletingConversation.value
);

function resetDeleteConversationModal() {
  selectedConversationId.value = null;
  isDeletingConversation.value = false;
}

function closeDeleteConversationModal() {
  if (isDeletingConversation.value) return;
  showDeleteConversationPopup.value = false;
  resetDeleteConversationModal();
}

async function deleteConversation() {
  if (!canDeleteSelectedConversation.value) return;

  isDeletingConversation.value = true;
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    showDeleteConversationPopup.value = false;
    resetDeleteConversationModal();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    isDeletingConversation.value = false;
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  showDeleteConversationPopup.value = true;
};

// Aceleres: modo seleção (estilo WhatsApp) — disparado pelo menu 3-dots
// no ChatListHeader; substitui o hover-to-select que ficava no avatar.
const selectionMode = ref(false);
const enterSelectionMode = () => {
  selectionMode.value = true;
};
const exitSelectionMode = () => {
  selectionMode.value = false;
  resetBulkActions();
};

const bulkMarkUnread = async () => {
  const ids = [...selectedConversations.value];
  if (!ids.length) return;
  await Promise.all(
    ids.map(id => store.dispatch('markMessagesUnread', { id }).catch(() => {}))
  );
  resetBulkActions();
  selectionMode.value = false;
};
const bulkMute = async () => {
  const ids = [...selectedConversations.value];
  if (!ids.length) return;
  await Promise.all(
    ids.map(id => store.dispatch('muteConversation', id).catch(() => {}))
  );
  resetBulkActions();
  selectionMode.value = false;
};
const bulkResolve = async () => {
  const count = selectedConversations.value.length;
  if (!count) return;
  const label = count === 1 ? 'esta conversa' : `estas ${count} conversas`;
  if (
    !window.confirm(
      `Concluir ${label}? Esta ação removerá ${
        count === 1 ? 'a conversa' : 'as conversas'
      } das caixas de atendimento ativo.`
    )
  ) {
    return;
  }
  await onUpdateConversations(wootConstants.STATUS_TYPE.RESOLVED, null);
  selectionMode.value = false;
};
provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignSelf', onAssignSelf);
provide('startConversation', onStartConversation);
provide('assignMine', onAssignMine);
provide('assignPersonal', onAssignPersonal);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);
provide('aclSelectionMode', selectionMode);

watch(
  () => route.query[ACELERES_BOX_QUERY_PARAM],
  () => syncAssigneeTabFromRoute({ immediateStats: true })
);

watch(shouldMergeArchivedIntoConcluded, isMobileViewport => {
  if (!isMobileViewport) {
    return;
  }

  if (activeAssigneeTab.value === ARCHIVED_ASSIGNEE_TAB) {
    updateAssigneeTab(CONCLUDED_ASSIGNEE_TAB);
    return;
  }

  if (
    normalizeAceleresBoxQuery(route.query[ACELERES_BOX_QUERY_PARAM]) ===
    'arquivadas'
  ) {
    setAceleresBoxQuery(CONCLUDED_ASSIGNEE_TAB);
  }
});

watch(activeTeam, () => resetAndFetchData());

// Aceleres: caso o papel do usuario so fique disponivel apos a montagem
// (ex.: refresh com store repopulado de forma assincrona), reaplica o
// default correto da aba enquanto o usuario nao escolheu uma manualmente.
watch(
  () => store.getters.getCurrentRole,
  role => {
    if (aceleresUserPickedTab.value) return;
    // Aceleres 4 caixas: todos (gestor e vendedor) abrem em NOVOS. Antes isso
    // forçava 'all' p/ gestor, que não existe mais nos items → activeAssigneeTabCount
    // achava undefined e estourava, quebrando a paginação/conversas.
    const desired = 'novos';
    if (activeAssigneeTab.value !== desired) {
      activeAssigneeTab.value = desired;
      resetAndFetchData();
    }
  }
);

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationStatusSnapshot, () => {
  (allConversationsList.value || [])
    .filter(
      conversation =>
        conversation?.status === wootConstants.STATUS_TYPE.RESOLVED
    )
    .forEach(synchronizeConversationInAceleresCaches);

  chatsOnView.value = conversationList.value;
  cacheCurrentAceleresConversationList();
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});

// === Aceleres: largura redimensionável da lista de conversas ===
const ACL_LIST_WIDTH_KEY = 'acl_conv_list_width';
const ACL_LIST_WIDTH_DEFAULT = 497;
const ACL_LIST_WIDTH_MIN = 320;
const ACL_LIST_WIDTH_MAX = 900;
const aclReadStoredWidth = () => {
  try {
    const raw = parseInt(localStorage.getItem(ACL_LIST_WIDTH_KEY) || '', 10);
    if (
      Number.isFinite(raw) &&
      raw >= ACL_LIST_WIDTH_MIN &&
      raw <= ACL_LIST_WIDTH_MAX
    ) {
      return raw;
    }
  } catch (e) {
    /* ignore */
  }
  return ACL_LIST_WIDTH_DEFAULT;
};
const aclListWidth = ref(aclReadStoredWidth());
const isAclResizing = ref(false);
let aclDragStartX = 0;
let aclDragStartW = 0;
const onAclResizeMove = e => {
  const dx = e.clientX - aclDragStartX;
  let w = aclDragStartW + dx;
  if (w < ACL_LIST_WIDTH_MIN) w = ACL_LIST_WIDTH_MIN;
  if (w > ACL_LIST_WIDTH_MAX) w = ACL_LIST_WIDTH_MAX;
  aclListWidth.value = w;
};
const onAclResizeEnd = () => {
  isAclResizing.value = false;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  window.removeEventListener('mousemove', onAclResizeMove);
  window.removeEventListener('mouseup', onAclResizeEnd);
  try {
    localStorage.setItem(ACL_LIST_WIDTH_KEY, String(aclListWidth.value));
  } catch (e) {
    /* ignore */
  }
};
const onAclResizeStart = e => {
  isAclResizing.value = true;
  aclDragStartX = e.clientX;
  aclDragStartW = aclListWidth.value;
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
  window.addEventListener('mousemove', onAclResizeMove);
  window.addEventListener('mouseup', onAclResizeEnd);
};
const onAclResizeReset = () => {
  aclListWidth.value = ACL_LIST_WIDTH_DEFAULT;
  try {
    localStorage.setItem(ACL_LIST_WIDTH_KEY, String(ACL_LIST_WIDTH_DEFAULT));
  } catch (e) {
    /* ignore */
  }
};
onUnmounted(() => {
  window.removeEventListener('mousemove', onAclResizeMove);
  window.removeEventListener('mouseup', onAclResizeEnd);
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap conversas bg-n-surface-1 acl-conv-list-resizable relative"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : '',
    ]"
    :style="!isOnExpandedLayout ? { width: aclListWidth + 'px' } : null"
  >
    <slot />
    <StarredMessagesPanel
      v-if="showStarredMessages"
      @close="closeStarredMessages"
      @open-message="openStarredMessage"
    />
    <ChatListHeader
      v-if="!showStarredMessages"
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFiltersForUI"
      :has-active-folders="hasActiveFolders"
      :active-assignee-tab="activeAssigneeTab"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="
        shouldShowChatListLoadingIndicator && !conversationList.length
      "
      :is-floating-header="isDashboardHomeRoute"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @enter-selection-mode="enterSelectionMode"
      @open-starred-messages="openStarredMessages"
      @open-archived="updateAssigneeTab(CONCLUDED_ASSIGNEE_TAB)"
      @new-conversation="openHeaderNewConversation"
    />
    <AceleresNewConversationPanel
      v-if="!showStarredMessages"
      :show="showNewConversationPanel"
      :account-id="accountIdForQuickSearch"
      :teleport-target="newConversationPanelTeleportTarget"
      :close-on-new-contact-back="showNewContactInEmptyState"
      @close="closeHeaderNewConversation"
      @start-phone-conversation="startConversationFromPanel"
      @open-contact-chat="openContactChatFromPanel"
      @edit-contact="editContactFromPanel"
    />
    <AceleresStartConversationFlow
      ref="startConversationFlowRef"
      :account-id="accountIdForQuickSearch"
      @started="closeQuickSearch"
    />
    <div
      v-show="!showStarredMessages"
      class="acl-dashboard-flow flex-1 min-h-0 flex flex-col"
      :class="{
        'acl-dashboard-flow--with-hero': isDashboardHomeRoute,
      }"
    >
      <TeleportWithDirection
        v-if="showAddFoldersModal"
        to="#saveFilterTeleportTarget"
      >
        <SaveCustomView
          v-model="appliedFilter"
          :custom-views-query="foldersQuery"
          :open-last-saved-item="openLastSavedItemInFolder"
          @close="onCloseAddFoldersModal"
        />
      </TeleportWithDirection>

      <DeleteCustomViews
        v-if="showDeleteFoldersModal"
        v-model:show="showDeleteFoldersModal"
        :active-custom-view="activeFolder"
        :custom-views-id="foldersId"
        :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
        @close="onCloseDeleteFoldersModal"
      />

      <!-- Aceleres: barra de busca rápida com dropdown live -->
      <div class="acl-quick-search-wrap relative">
        <div class="acl-search-pill">
          <svg
            class="acl-search-pill__icon"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            v-model="quickSearch"
            @input="onQuickSearchInput"
            @keydown.enter="onQuickSearchEnter"
            @focus="quickSearchOpen = quickSearch.length >= 1"
            type="text"
            placeholder="Buscar nome ou telefone"
            class="acl-search-pill__input"
          />
          <button
            v-if="quickSearch.length > 0"
            @click="closeQuickSearch"
            type="button"
            class="acl-search-pill__clear"
            aria-label="Limpar busca"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Aceleres: resultados da busca inline na coluna (estilo WhatsApp) -->
      <div
        v-if="isSearchActive"
        class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-2"
      >
        <div
          v-if="quickSearchLoading"
          class="px-3 py-3 text-xs text-n-slate-11"
        >
          Buscando…
        </div>

        <div v-if="quickSearchConversations.length" class="px-1 py-1">
          <div class="px-3 py-1 text-[11px] font-semibold text-n-slate-11">
            {{ quickSearchConversationHeading }}
          </div>
          <button
            v-for="cv in quickSearchConversations.slice(0, 20)"
            :key="'cv' + cv.id"
            @click="openConvFromQuick(cv)"
            class="w-full text-left px-2 py-2 rounded-lg flex items-center gap-3 transition-colors"
            :class="{
              'active bg-n-background': isQuickSearchConversationActive(cv.id),
              'hover:bg-n-alpha-2': !isQuickSearchConversationActive(cv.id),
            }"
          >
            <Avatar
              :name="cv.contact?.name || cv.contact?.phone_number || '?'"
              :src="cv.contact?.thumbnail"
              :size="40"
              rounded-full
            />
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{
                  cv.contact?.name ||
                  cv.contact?.phone_number ||
                  '(sem contato)'
                }}
              </div>
              <div class="text-xs text-n-slate-10 truncate">
                {{ cv.contact?.phone_number || '' }}
              </div>
              <div
                v-if="cv.message?.content"
                class="text-xs text-n-slate-10 truncate"
              >
                {{ cv.message.content }}
              </div>
            </div>
          </button>
        </div>

        <div
          v-if="
            quickSearchConversations.length &&
            quickSearchContacts.length
          "
          class="my-1 mx-3 h-px bg-n-weak"
        ></div>

        <div v-if="quickSearchContacts.length" class="px-1 py-1">
          <div class="px-3 py-1 text-[11px] font-semibold text-n-slate-11">
            Contatos
          </div>
          <button
            v-for="ct in quickSearchContacts.slice(0, 20)"
            :key="'c' + ct.id"
            @click="openContactFromQuick(ct)"
            class="w-full text-left px-2 py-2 rounded-lg hover:bg-n-alpha-2 flex items-center gap-3"
          >
            <Avatar
              :name="ct.name || ct.phone_number || '?'"
              :src="ct.thumbnail"
              :size="40"
              rounded-full
            />
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ ct.name || ct.phone_number }}
              </div>
              <div class="text-xs text-n-slate-10 truncate">
                {{ ct.phone_number }}
              </div>
            </div>
          </button>
        </div>

        <div
          v-if="quickSearchNoResults && quickSearchIsPhone"
          class="px-3 pt-2"
        >
          <button
            :disabled="isStartingConversation"
            class="w-full px-3 py-2.5 bg-n-brand text-white rounded-lg text-xs font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            @click="openNewConversationForPhone"
          >
            <span class="i-lucide-message-circle-plus size-3.5" />
            {{
              isStartingConversation
                ? 'Abrindo...'
                : 'Iniciar nova conversa com ' + quickSearch
            }}
          </button>
        </div>

        <div
          v-else-if="quickSearchNoResults"
          class="px-3 py-6 text-center text-xs text-n-slate-11"
        >
          Nenhum resultado.
        </div>
      </div>

      <div v-show="!isSearchActive" class="flex-1 min-h-0 flex flex-col">
        <div class="acl-filters-bar">
          <ChatTypeTabs
            v-if="!hasAppliedFiltersOrActiveFoldersForUI"
            :items="assigneeTabItems"
            :active-tab="activeAssigneeTab"
            :active-filter-count="aceleresFilterCount"
            :is-filter-menu-open="aceleresFilterMenuOpen"
            is-compact
            @chat-tab-change="updateAssigneeTab"
            @sort-change="onSortChange"
            @filter-click="
              anchorEl =>
                aceleresDropdownFilterRef?.openModal('popover', anchorEl)
            "
          />
          <Transition name="acl-unread-subfilters-fade">
            <div
              v-if="shouldShowUnreadSubfilters"
              class="acl-unread-subfilters"
            >
              <button
                type="button"
                class="acl-unread-subfilter-btn"
                :class="{
                  'acl-unread-subfilter-btn--active':
                    activeUnreadSubfilter === UNREAD_SUBFILTER_ALL,
                }"
                :aria-pressed="activeUnreadSubfilter === UNREAD_SUBFILTER_ALL"
                @click="updateUnreadSubfilter(UNREAD_SUBFILTER_ALL)"
              >
                Todos
              </button>
              <button
                type="button"
                class="acl-unread-subfilter-btn acl-unread-subfilter-btn--unread"
                :class="{
                  'acl-unread-subfilter-btn--active':
                    activeUnreadSubfilter === UNREAD_SUBFILTER_ONLY,
                }"
                :aria-pressed="activeUnreadSubfilter === UNREAD_SUBFILTER_ONLY"
                @click="updateUnreadSubfilter(UNREAD_SUBFILTER_ONLY)"
              >
                <span>Não lidos</span>
              </button>
            </div>
          </Transition>
          <AceleresDropdownFilter
            v-if="!hasActiveFolders"
            ref="aceleresDropdownFilterRef"
            :inboxes="inboxesList"
            :labels="labels"
            :agents="agentList"
            :teams="teamsList"
            :show-inbox-filter="isManagerRole"
            @apply="onAceleresFilterApply"
            @clear="onAceleresFilterClear"
            @count-change="aceleresFilterCount = $event"
            @open-state-change="aceleresFilterMenuOpen = $event"
          />
        </div>

        <button
          v-if="!hasAppliedFiltersOrActiveFoldersForUI"
          type="button"
          class="acl-archived-row"
          :class="{
            'acl-archived-row--active':
              activeAssigneeTab === CONCLUDED_ASSIGNEE_TAB,
          }"
          @click="updateAssigneeTab(CONCLUDED_ASSIGNEE_TAB)"
        >
          <span class="acl-archived-row__icon" aria-hidden="true">
            <span class="i-lucide-circle-check-big" />
          </span>
          <span class="acl-archived-row__label">{{ CONCLUDED_TAB_LABEL }}</span>
        </button>

        <!-- Aceleres: empty state contextual. Quando o filtro "Atendidos pela IA"
         está ativo e a lista vem vazia (sem resultados OU erro 422 absorvido
         pela action), mostramos mensagem específica em vez do 404 genérico. -->
        <div
          v-if="!shouldShowChatListLoadingIndicator && !conversationList.length"
          class="flex flex-col overflow-auto justify-center items-center p-6 gap-3 text-center"
        >
          <!-- Aceleres: falha de rede/5xx nao pode se disfarcar de "nenhuma
           conversa" — mostra erro real e deixa recarregar sem depender de F5. -->
          <template v-if="chatListHasError">
            <span class="i-lucide-wifi-off size-8 text-n-slate-10" />
            <p class="text-sm font-medium text-n-slate-12">
              Não foi possível carregar as conversas.
            </p>
            <p class="text-xs text-n-slate-10">
              Verifique sua conexão e tente novamente.
            </p>
            <button
              type="button"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-n-slate-12 bg-n-slate-3 hover:bg-n-slate-4"
              @click="retryFetchConversations"
            >
              <span class="i-lucide-refresh-cw size-3.5" />
              Tentar novamente
            </button>
          </template>
          <template v-else-if="appliedIaFilter">
            <span class="i-lucide-bot size-8 text-n-slate-10"></span>
            <p class="text-sm font-medium text-n-slate-12">
              Nenhuma conversa atendida pela IA foi encontrada.
            </p>
            <p class="text-xs text-n-slate-10">
              Ajuste o filtro e tente novamente.
            </p>
          </template>
          <template v-else-if="labelFilterEmptyMessage">
            <span class="i-lucide-tags size-8 text-n-slate-10" />
            <p class="text-sm font-medium text-n-slate-12">
              {{ labelFilterEmptyMessage }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ $t('CHAT_LIST.FILTERED_EMPTY.ADJUST') }}
            </p>
          </template>
          <p v-else class="text-sm text-n-slate-11">
            {{ $t('CHAT_LIST.LIST.404') }}
          </p>
        </div>
        <!-- Aceleres: ConversationBulkActions nativo desabilitado em favor da
         AclSelectionToolbar (WhatsApp-style). Mantemos selectedConversations
         compartilhado (vuex bulkActions) pra reuso dos endpoints. -->
        <AclSelectionToolbar
          v-if="selectionMode"
          :count="selectedConversations.length"
          @exit="exitSelectionMode"
          @mark-unread="bulkMarkUnread"
          @mute="bulkMute"
          @resolve="bulkResolve"
        />
        <div
          ref="conversationListRef"
          class="flex-1 min-h-0 overflow-y-auto conversations-list mt-5"
          :class="{ '!overflow-hidden': isContextMenuOpen }"
        >
          <Virtualizer
            ref="virtualListRef"
            v-slot="{ item, index }"
            :data="conversationList"
          >
            <ConversationItem
              :key="item.id"
              :source="item"
              :label="activeConversationLabel"
              :team-id="teamId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :active-assignee-tab="activeAssigneeTab"
              :show-assignee="showAssigneeInConversationCard"
              :data-index="index"
              @select-conversation="selectConversation"
              @de-select-conversation="deSelectConversation"
            />
          </Virtualizer>
          <div
            v-if="shouldShowChatListLoadingIndicator"
            class="flex justify-center my-4"
          >
            <Spinner class="text-n-brand" />
          </div>
          <p
            v-else-if="showEndOfListMessage"
            class="p-4 text-center text-n-slate-11"
          >
            {{ $t('CHAT_LIST.EOF') }}
          </p>
          <IntersectionObserver
            v-else
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </div>
      </div>
      <Teleport to="body">
        <Transition name="team-delete-modal">
          <div
            v-if="showDeleteConversationPopup"
            class="teams-page__delete-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(15,23,42,0.55)] p-4"
            tabindex="-1"
            @click.self="closeDeleteConversationModal"
            @keydown.esc.stop.prevent="closeDeleteConversationModal"
          >
            <div
              class="conversation-delete-modal relative max-h-full overflow-auto bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu rounded-xl w-[37.5rem]"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="conversation-delete-modal-title"
              aria-describedby="conversation-delete-modal-description"
            >
              <div class="conversation-delete-modal__content">
                <h2
                  id="conversation-delete-modal-title"
                  class="conversation-delete-modal__title"
                >
                  {{ selectedConversationDeleteTitle }}
                </h2>
                <p
                  id="conversation-delete-modal-description"
                  class="conversation-delete-modal__description"
                >
                  {{ selectedConversationDeleteDescription }}
                </p>

                <div class="conversation-delete-modal__actions">
                  <button
                    type="button"
                    class="conversation-delete-modal__cancel"
                    :disabled="isDeletingConversation"
                    @click="closeDeleteConversationModal"
                  >
                    {{ conversationDeleteModalText.cancel }}
                  </button>
                  <button
                    type="button"
                    class="conversation-delete-modal__confirm"
                    :disabled="!canDeleteSelectedConversation"
                    @click="deleteConversation"
                  >
                    {{
                      isDeletingConversation
                        ? conversationDeleteModalText.confirming
                        : conversationDeleteModalText.confirm
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
      <TeleportWithDirection
        v-if="showAdvancedFilters"
        to="#conversationFilterTeleportTarget"
      >
        <ConversationFilter
          v-if="hasActiveFolders"
          v-model="appliedFilter"
          :folder-name="activeFolderName"
          is-folder-view
          @apply-filter="onApplyFilter"
          @update-folder="onUpdateSavedFilter"
          @close="closeAdvanceFiltersModal"
        />
        <QuickConversationFilter
          v-else
          @apply-filter="onApplyFilter"
          @close="closeAdvanceFiltersModal"
        />
      </TeleportWithDirection>
      <ConversationResolveAttributesModal
        ref="resolveAttributesModalRef"
        @submit="handleResolveWithAttributes"
      />
    </div>
    <div
      v-if="!isOnExpandedLayout"
      class="acl-conv-list-resize-handle"
      :class="{ 'acl-conv-list-resize-handle--active': isAclResizing }"
      @mousedown.prevent="onAclResizeStart"
      @dblclick="onAclResizeReset"
    />
  </div>
</template>

<style scoped>
.conversation-delete-modal {
  z-index: 10000;
  width: min(52rem, calc(100vw - 2rem));
  max-height: calc(100vh - 2rem);
  border: 0;
  border-radius: 2rem;
  background: #ffffff;
  padding: 3rem 3rem 2.25rem;
}

.conversation-delete-modal__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.conversation-delete-modal__title {
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.25;
}

.conversation-delete-modal__description {
  max-width: 30rem;
  margin: 1.5rem 0 0;
  color: #6b7280;
  font-size: 1.125rem;
  line-height: 1.5;
}

.conversation-delete-modal__actions {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 1.5rem;
  padding-top: 3rem;
}

.conversation-delete-modal__cancel,
.conversation-delete-modal__confirm {
  display: inline-flex;
  min-width: 9.5rem;
  min-height: 3.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.75rem 1.75rem;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.25;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    opacity 0.15s ease;
}

.conversation-delete-modal__cancel {
  border: 0;
  background: transparent;
  color: #278c68;
}

.conversation-delete-modal__cancel:hover:not(:disabled) {
  background: #f0fdf4;
  color: #19704f;
}

.conversation-delete-modal__confirm {
  border: 0;
  background: #f0003c;
  color: #ffffff;
}

.conversation-delete-modal__confirm:hover:not(:disabled) {
  background: #cf0034;
}

.conversation-delete-modal__cancel:disabled,
.conversation-delete-modal__confirm:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.team-delete-modal-enter-active,
.team-delete-modal-leave-active {
  transition: opacity 0.2s ease;
}

.team-delete-modal-enter-from,
.team-delete-modal-leave-to {
  opacity: 0;
}

:global(.dark) .conversation-delete-modal {
  background: #111315;
}

:global(.dark) .conversation-delete-modal__cancel,
:global(.dark) .conversation-delete-modal__title,
:global(.dark) .conversation-delete-modal__cancel {
  color: #f9fafb;
}

:global(.dark) .conversation-delete-modal__description {
  color: #a1a1aa;
}

:global(.dark) .conversation-delete-modal__cancel:hover:not(:disabled) {
  background: #252b31;
}

@media (max-width: 640px) {
  .conversation-delete-modal {
    width: min(52rem, calc(100vw - 1rem));
    padding: 2rem 1.25rem 1.5rem;
  }

  .conversation-delete-modal__actions {
    flex-direction: column-reverse;
  }

  .conversation-delete-modal__cancel,
  .conversation-delete-modal__confirm {
    width: 100%;
  }
}

.acl-dashboard-flow {
  position: relative;
}

.acl-dashboard-flow--with-hero {
  padding-top: 0;
}

@media (max-width: 767px) {
  .acl-dashboard-flow--with-hero {
    padding-top: 0;
  }
}

.acl-filters-bar {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-bottom: 0 !important;
  box-shadow: none !important;
}

.acl-filters-bar :deep(.acl-pill-tabs) {
  border-bottom: 0 !important;
  box-shadow: none !important;
}

.acl-unread-subfilters {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 6px 28px;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 0 !important;
  box-shadow: none !important;
  scrollbar-width: none;
}
.conversations-list-wrap .acl-filters-bar,
.conversations-list-wrap .acl-unread-subfilters {
  border: 0 !important;
  border-block-end: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
}
.conversations-list-wrap.conversas.acl-conv-list-resizable .acl-filters-bar {
  border-bottom: none !important;
}
.conversations-list-wrap .acl-filters-bar::before,
.conversations-list-wrap .acl-filters-bar::after,
.conversations-list-wrap .acl-unread-subfilters::before,
.conversations-list-wrap .acl-unread-subfilters::after {
  display: none !important;
  content: none !important;
}
.acl-unread-subfilters::-webkit-scrollbar {
  display: none;
}
.acl-unread-subfilter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  min-height: 30px;
  padding: 7px 12px;
  border: 1px solid #d4d4d4;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  color: #767676;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  transition:
    background-color 0.12s,
    color 0.12s,
    border-color 0.12s,
    box-shadow 0.12s;
}
.acl-unread-subfilter-btn:hover {
  color: #525252;
  border-color: #b5b5b5;
  background: rgba(255, 255, 255, 0.92);
}
.acl-unread-subfilter-btn--active,
.acl-unread-subfilter-btn--active:hover {
  background: #d9fdd3;
  color: #15603e;
  border-color: #15603e;
  box-shadow: 0 0 0 1px rgba(21, 96, 62, 0.06);
}
.acl-unread-subfilter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #25d366;
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  line-height: 18px;
}
.acl-unread-subfilters-fade-enter-active,
.acl-unread-subfilters-fade-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}
.acl-unread-subfilters-fade-enter-from,
.acl-unread-subfilters-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
.acl-archived-row {
  display: none;
}
:global(.dark) .acl-unread-subfilter-btn {
  background: rgba(17, 19, 21, 0.72);
  border-color: #3f3f46;
  color: #a1a1aa;
}
:global(.dark) .acl-unread-subfilter-btn:hover {
  background: rgba(24, 24, 27, 0.92);
  border-color: #52525b;
  color: #d4d4d8;
}
:global(.dark) .acl-unread-subfilter-btn--active,
:global(.dark) .acl-unread-subfilter-btn--active:hover {
  background: rgba(21, 96, 62, 0.25);
  border-color: #15603e;
  color: #86efac;
}
.acl-conv-list-resizable {
  position: relative;
  min-height: 0;
  overflow: hidden;
}
.acl-conv-list-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  z-index: 50;
  background: transparent;
  transition: background-color 0.12s;
}
.acl-conv-list-resize-handle:hover,
.acl-conv-list-resize-handle--active {
  background: rgba(100, 116, 139, 0.35);
}
.acl-quick-search-wrap {
  padding: 6px 0;
}
.acl-search-pill {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 14px !important;
  background: rgba(194, 189, 184, 0.15);
  border: none;
  border-radius: 50px;
  box-shadow: inset 0 0 0 0 transparent;
  transition: box-shadow 0.12s;
}
.acl-search-pill:hover {
  box-shadow: inset 0 0 0 1px #c4c4c4;
}
.acl-search-pill:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}
.acl-search-pill__icon {
  flex-shrink: 0;
  color: #8a8a8a;
}
.acl-search-pill__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  background: transparent !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  padding: 0;
  margin: 0;
  font-size: 13px;
  color: #1f2937;
  line-height: 40px;
}
.acl-search-pill__input::placeholder {
  color: #8a8a8a;
}
.acl-search-pill__input:focus,
.acl-search-pill__input:focus-visible,
.acl-search-pill__input:focus-within {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
}
.acl-search-pill__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  border-radius: 9999px;
  color: #8a8a8a;
  cursor: pointer;
  padding: 0;
  transition:
    background-color 0.12s,
    color 0.12s;
}
.acl-search-pill__clear:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #4b5563;
}
:global(.dark) .acl-search-pill {
  background: rgba(255, 255, 255, 0.04);
}
:global(.dark) .acl-search-pill:hover {
  box-shadow: inset 0 0 0 1px #3f3f46;
}
:global(.dark) .acl-search-pill__icon,
:global(.dark) .acl-search-pill__input::placeholder,
:global(.dark) .acl-search-pill__clear {
  color: #a1a1aa;
}
:global(.dark) .acl-search-pill__input {
  color: #e5e5e5;
}
:global(.dark) .acl-search-pill__clear:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #e5e5e5;
}

@media (max-width: 767px) {
  .acl-archived-row {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 44px;
    gap: 14px;
    padding: 8px 18px 8px 28px;
    border: 0;
    background: transparent;
    color: #54656f;
    text-align: left;
  }

  .acl-archived-row:hover,
  .acl-archived-row--active {
    background: rgba(17, 27, 33, 0.04);
    color: #0b8457;
  }

  .acl-archived-row__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    color: inherit;
  }

  .acl-archived-row__icon > span {
    width: 18px;
    height: 18px;
    background-color: currentColor;
  }

  .acl-archived-row__label {
    flex: 1 1 auto;
    min-width: 0;
    color: #111b21;
    font-size: 13px;
    font-weight: 650;
    line-height: 1.2;
  }

  .acl-archived-row--active .acl-archived-row__label {
    color: #0b8457;
  }

  .acl-archived-row__count {
    flex: 0 0 auto;
    color: #0b8457;
    font-size: 12px;
    font-weight: 700;
  }

  :global(.dark) .acl-archived-row {
    color: #8696a0;
  }

  :global(.dark) .acl-archived-row:hover,
  :global(.dark) .acl-archived-row--active {
    background: rgba(233, 237, 239, 0.05);
    color: #25d366;
  }

  :global(.dark) .acl-archived-row__label {
    color: #e9edef;
  }

  :global(.dark) .acl-archived-row--active .acl-archived-row__label,
  :global(.dark) .acl-archived-row__count {
    color: #25d366;
  }

  .conversations-list {
    padding-bottom: calc(76px + env(safe-area-inset-bottom, 0px));
    scroll-padding-bottom: calc(76px + env(safe-area-inset-bottom, 0px));
  }
}
</style>
